import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { X } from "lucide-react";
import { fetchChatPing, isChatStandalone } from "./apiBase";
import { isChatHub2Desk } from "./chatHub2";
import { ChatPwaOnboard } from "./components/ChatPwaOnboard";
import { installChatPushFocusGuard, syncChatWebPushOnBoot } from "./lib/chatWebPushRuntime";
import { ChatBootSplash } from "./components/ChatBootSplash";
import { ChatConfirmDialog } from "./components/ChatConfirmDialog";
import { type ChatGifItem } from "./components/ChatEmojiGifPicker";
import { ChatProfileModal } from "./components/ChatProfileModal";
import { ImageLightbox } from "./components/ImageLightbox";
import { ChatDestFolderPicker } from "./components/ChatDestFolderPicker";
import { ChatEmojiBurstLayer, useEmojiBursts } from "./components/ChatEmojiBurst";
import { MessageContextMenu } from "./components/MessageContextMenu";
import { ChatForwardPicker } from "./components/ChatForwardPicker";
import { ChatAurizOverlay } from "./components/ChatAurizOverlay";
import { ChatThemeProvider } from "./hooks/useChatTheme";
import { useChatMobileHistory } from "./hooks/useChatMobileHistory";
import { useChatEdgeSwipeBack } from "./hooks/useChatEdgeSwipeBack";
import { useChatRealtime } from "./hooks/useChatRealtime";
import { ChatCallOverlay, type ChatCallChrome, type ChatCallSession } from "./components/ChatCallOverlay";
import { ChatIncomingCallBanner } from "./components/ChatIncomingCallBanner";
import { ChatConversationList } from "./components/ChatConversationList";
import { ChatThread } from "./components/ChatThread";
import { ChatInfoPanel } from "./components/ChatInfoPanel";
import { ChatGuestInviteModal } from "./components/ChatGuestInviteModal";
import { ChatGuestJoin } from "./components/ChatGuestJoin";
import { listChatMeetings, scheduleChatMeeting, type ChatMeeting } from "@/lib/meetingsApi";
import {
  copyText,
  createChatGuestInvite,
  readGuestToken,
} from "@/lib/guestsApi";
import {
  ChatCallPickModal,
  ChatNewDmModal,
  ChatNewGroupModal,
  ChatNewVideoModal,
} from "./components/ChatNewModals";
import { resolveChatAvatarUrl } from "@/lib/chatAvatar";
import { conversationTitle, isImageAttachment, memberLabel, parseLastSeenMap, type ChatMobilePane } from "./lib/chatFormat";
import {
  applyChatSidebarOrder,
  isChatSidebarOrderEmpty,
  parseChatSidebarOrder,
  readChatSidebarOrder,
  toggleChatSidebarPin,
  writeChatSidebarOrder,
} from "./lib/chatSidebarOrder";
import {
  applyNickToColleague,
  applyNickToConversation,
  chatNickOrOfficial,
  isChatNickMapEmpty,
  parseChatNicknames,
  readChatNicknames,
  setChatNickname,
  writeChatNicknames,
  type ChatNickMap,
} from "./lib/chatNicknames";
import { readChatThreadCache, writeChatThreadCache } from "./lib/chatThreadCache";
import { formatCopyText, formatForwardBody } from "./lib/chatMentions";
import { aurizEligibility } from "./lib/chatAuriz";
import { endImpactWindup, primeChatEcho, primeChatImpact } from "./components/ChatSendEffects";
import {
  persistChatWallpaper,
  readChatWallpaper,
  type ChatWallpaperPrefs,
} from "./lib/theme/chatWallpapers";
import {
  chatCallAccept,
  chatCallAddParticipant,
  chatCallActive,
  chatCallHangup,
  chatCallInvite,
  chatCallJoin,
  chatCallMiss,
  chatCallReject,
  chatCallTouch,
  createConversation,
  deleteConversation,
  deleteMessage,
  editMessage,
  fetchChatProfile,
  saveChatSidebarOrder,
  saveChatNicknames,
  listColleagues,
  listConversations,
  listMessages,
  markRead,
  markUnread,
  reactToMessage,
  removeConversationMember,
  addConversationMember,
  renameConversation,
  sendAuriz,
  sendMessage,
  sendMessageWithFiles,
  setTyping,
  fetchChatPresenceSelf,
  normalizeChatPresence,
  isChatPeerBusyInCall,
  chatCallIsJoinable,
  isOpenVisioConversation,
  type ChatPresenceStatus,
  updateConversation,
  CHAT_MAX_UPLOAD_BYTES,
  CHAT_MAX_VIDEO_UPLOAD_LABEL,
  chatFileMaxBytes,
  chatUploadTooLargeMessage,
  isChatVideoFile,
  type ChatColleague,
  type ChatConversation,
  type ChatAttachment,
  type ChatMessage,
  type ChatMessageEffect,
  type ChatProfile,
  type ChatReaction,
  type ChatReceipt,
} from "@/lib/chatApi";
import { closeChatPoll, createChatPoll, isChatPollMessage, messagePoll, voteChatPoll } from "@/lib/chatPolls";
import {
  cachedChatFile,
  collectMessageSaveTargets,
  downloadChatAttachment,
  downloadChatAttachments,
  prefetchChatAttachment,
  saveChatAttachmentsToCoffre,
  saveChatAttachmentsToPhotos,
  shareOrDownloadFile,
} from "@/lib/suite/saveChatAttachment";
import {
  playChatAuriz,
  playChatDeletePop,
  playChatEmojiPop,
  playChatLongPressPop,
  playChatNotificationSound,
  playChatSendPop,
  unlockChatLongPressPop,
  unlockChatNotificationSound,
} from "@/lib/chatNotificationSound";
import {
  playChatCallCalling,
  playChatCallClose,
  playChatCallOpen,
  playChatCallRingtone,
  stopAllChatCallSounds,
  stopChatCallCalling,
  stopChatCallRingtone,
} from "@/lib/chatCallSounds";
import { expandEmoticons, expandEmoticonsWithCaret } from "@/lib/emoticons";
import {
  clearStoredSuiteUserIds,
  fetchSsoVerify,
  isChatEmbeddedInHub,
  isSuiteSessionErrorCode,
  readStoredSuiteUserId,
  resetChatSessionGateEmit,
  syncSuiteUserIdFromQuery,
  type SuiteUserProfile,
} from "./suiteSession";

type BridgeState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ok"; serverUserId: number; message?: string }
  | { status: "error"; message: string };

type GateState = "boot" | "checking" | "ready" | "need_login" | "forbidden" | "error";

function ChatShell({
  suiteUserId,
  profile,
}: {
  suiteUserId: string;
  profile: SuiteUserProfile;
}) {
  const selfId = profile.id;
  const { shower, local, spawnLocalBurst } = useEmojiBursts();
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [sidebarOrder, setSidebarOrder] = useState(() => readChatSidebarOrder(profile.id));
  const [nicknames, setNicknames] = useState<ChatNickMap>(() => readChatNicknames(profile.id));
  const nicknamesRef = useRef<ChatNickMap>(nicknames);
  nicknamesRef.current = nicknames;
  const [colleagues, setColleagues] = useState<ChatColleague[]>([]);
  const [myClientIds, setMyClientIds] = useState<number[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [callSession, setCallSession] = useState<ChatCallSession | null>(null);
  const [callChrome, setCallChrome] = useState<ChatCallChrome>("immersive");
  const [incomingCall, setIncomingCall] = useState<{
    conversationId: number;
    callId: string;
    room: string;
    media: "audio" | "video";
    fromUserId: number;
    fromName: string;
  } | null>(null);
  const callSessionRef = useRef<ChatCallSession | null>(null);
  callSessionRef.current = callSession;
  const [callAnswered, setCallAnswered] = useState(false);
  const ringTimerRef = useRef<number | null>(null);
  const pendingIncomingCallRef = useRef<{
    conversationId: number;
    callId: string;
    room: string;
    media: "audio" | "video";
    fromUserId: number;
    fromName: string;
  } | null>(null);
  const clearRingTimer = () => {
    if (ringTimerRef.current != null) {
      window.clearTimeout(ringTimerRef.current);
      ringTimerRef.current = null;
    }
  };
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [meetings, setMeetings] = useState<ChatMeeting[]>([]);
  const [showTheme, setShowTheme] = useState(false);
  const [myStatus, setMyStatus] = useState<ChatPresenceStatus>("online");
  const [showPicker, setShowPicker] = useState(false);
  const [showNewDm, setShowNewDm] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showNewGuest, setShowNewGuest] = useState(false);
  const [showNewVideo, setShowNewVideo] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [groupTitle, setGroupTitle] = useState("");
  const [groupMembers, setGroupMembers] = useState<number[]>([]);
  const [groupAsVisioRoom, setGroupAsVisioRoom] = useState(false);
  const [suppressJoinCallId, setSuppressJoinCallId] = useState<string | null>(null);
  const [showCallPick, setShowCallPick] = useState(false);
  const [callPickMembers, setCallPickMembers] = useState<number[]>([]);
  const [callPickMedia, setCallPickMedia] = useState<"audio" | "video">("video");
  const [mobilePane, setMobilePane] = useState<ChatMobilePane>("list");
  const [typingMap, setTypingMap] = useState<Record<number, Set<number>>>({});
  const [presence, setPresence] = useState<Record<number, string>>({});
  const [lastSeen, setLastSeen] = useState<Record<number, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const hintTimer = useRef<number | null>(null);
  const [forwardMsg, setForwardMsg] = useState<ChatMessage | null>(null);
  const [forwardBusy, setForwardBusy] = useState(false);
  const [aurizBusy, setAurizBusy] = useState(false);
  const [aurizShow, setAurizShow] = useState<{
    fromName: string;
    mine: boolean;
    discreet: boolean;
  } | null>(null);
  const [chatProfile, setChatProfile] = useState<ChatProfile | null>(null);
  const [showProfile, setShowProfile] = useState(false);
  const [renameDraft, setRenameDraft] = useState("");
  const [editingMsgId, setEditingMsgId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [ctxMenu, setCtxMenu] = useState<{
    x: number;
    y: number;
    message: ChatMessage;
    sheet: boolean;
  } | null>(null);
  const [lightbox, setLightbox] = useState<{
    src: string;
    alt?: string;
    variant?: "image" | "avatar";
    attachment?: ChatAttachment;
  } | null>(null);
  const [savePicker, setSavePicker] = useState<{
    kind: "coffre" | "photos";
    atts: ChatAttachment[];
  } | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ChatConversation | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [removeMemberTarget, setRemoveMemberTarget] = useState<{
    userId: number;
    name: string;
  } | null>(null);
  const [removeMemberBusy, setRemoveMemberBusy] = useState(false);
  const [deleteMessageId, setDeleteMessageId] = useState<number | null>(null);
  const [deleteMessageBusy, setDeleteMessageBusy] = useState(false);
  const [wallpaperPrefs, setWallpaperPrefs] = useState<ChatWallpaperPrefs>(() =>
    readChatWallpaper(null)
  );
  const [sendEffectsOpen, setSendEffectsOpen] = useState(false);
  const [effectReplay, setEffectReplay] = useState<Record<number, number>>({});
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [flashReplyId, setFlashReplyId] = useState<number | null>(null);
  const threadDragDepthRef = useRef(0);
  const [threadFileDragOver, setThreadFileDragOver] = useState(false);
  const draftRef = useRef<HTMLTextAreaElement>(null);
  const longPressTimer = useRef<number | null>(null);
  const longPressOrigin = useRef<{ x: number; y: number } | null>(null);
  const longPressOpenedAt = useRef(0);
  const pendingOpenConvRef = useRef<number | null>(null);
  const pendingAurizRef = useRef<{ fromName: string } | null>(null);

  const mergeLastSeen = (raw?: Record<string, number> | Record<number, number> | null) => {
    if (!raw) return;
    const parsed = parseLastSeenMap(raw as Record<string, unknown>);
    if (Object.keys(parsed).length === 0) return;
    setLastSeen((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [id, unix] of Object.entries(parsed)) {
        const n = Number(id);
        if (next[n] !== unix) {
          next[n] = unix;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  const playIncomingAuriz = useCallback((fromName: string) => {
    const discreet = myStatus === "dnd" || myStatus === "meeting" || callSession != null;
    if (!discreet) {
      playChatAuriz();
      try {
        if (typeof navigator !== "undefined" && "vibrate" in navigator) {
          navigator.vibrate([16, 36, 20, 36, 16]);
        }
      } catch {
        /* ignore */
      }
    }
    setAurizShow({
      fromName: fromName.trim() || "Quelqu’un",
      mine: false,
      discreet,
    });
  }, [myStatus, callSession]);
  /** Ignore les clics fantômes juste après une action du menu message (réaction → photo). */
  const suppressMediaClickUntil = useRef(0);

  const { goBack: goBackMobile, dismissOverlay } = useChatMobileHistory({
    mobilePane,
    setMobilePane,
    showInfo,
    setShowInfo,
    lightboxOpen: lightbox != null,
    closeLightbox: () => setLightbox(null),
    ctxMenuOpen: ctxMenu != null,
    closeCtxMenu: () => setCtxMenu(null),
  });

  const threadPaneRef = useRef<HTMLElement | null>(null);
  useChatEdgeSwipeBack({
    enabled: mobilePane === "thread",
    targetRef: threadPaneRef,
    onBack: goBackMobile,
  });

  useEffect(() => {
    const unlock = () => unlockChatNotificationSound();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    if (lightbox?.attachment) prefetchChatAttachment(lightbox.attachment);
  }, [lightbox]);

  useEffect(() => {
    if (!ctxMenu) return;
    for (const att of collectMessageSaveTargets(ctxMenu.message)) {
      prefetchChatAttachment(att);
    }
  }, [ctxMenu]);
  const threadEndRef = useRef<HTMLDivElement>(null);
  const threadScrollRef = useRef<HTMLDivElement>(null);
  const threadScrollPinTimers = useRef<number[]>([]);
  const stickBottomUntilRef = useRef(0);
  const typingTimer = useRef<number | null>(null);
  const typingSent = useRef(false);
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;
  const listReadyRef = useRef(false);
  const lastIncallSnapRef = useRef(0);
  const overlayIncallRef = useRef<Set<number>>(new Set());
  const markUsersInCall = (ids: number[]) => {
    let any = false;
    for (const raw of ids) {
      const id = Number(raw);
      if (!Number.isFinite(id) || id < 1 || id === selfId) continue;
      overlayIncallRef.current.add(id);
      any = true;
    }
    if (!any) return;
    lastIncallSnapRef.current = Date.now();
    setPresence((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const id of overlayIncallRef.current) {
        if (next[id] !== "incall") {
          next[id] = "incall";
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  /** Effets déjà révélés cette session (évite rejouabilité à chaque ouverture). */
  const revealedEffectsRef = useRef(new Set<number>());
  /** Révélation différée : unread est remis à 0 dès le clic, avant le chargement du fil. */
  const pendingEffectRevealRef = useRef<{
    conversationId: number;
    messageId: number;
    effect: ChatMessageEffect;
  } | null>(null);

  const mobilePaneRef = useRef(mobilePane);
  mobilePaneRef.current = mobilePane;
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  /** Fil réellement visible (sur mobile, selectedId peut rester alors qu’on est sur la liste). */
  const isViewingConversation = useCallback((conversationId: number) => {
    if (selectedIdRef.current !== conversationId) return false;

    if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
      return mobilePaneRef.current === "thread";
    }
    return true;
  }, []);

  const clearThreadScrollPins = () => {
    for (const t of threadScrollPinTimers.current) window.clearTimeout(t);
    threadScrollPinTimers.current = [];
  };

  /** Colle le fil en bas ; retries car slide mobile / révélation / images changent la hauteur. */
  const pinThreadToBottom = useCallback((opts?: { retries?: boolean }) => {
    const apply = () => {
      const scroller = threadScrollRef.current;
      if (!scroller) return;
      const max = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      scroller.scrollTop = max;
      scroller.scrollTop = scroller.scrollHeight;
    };
    apply();
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
    if (!opts?.retries) return;
    stickBottomUntilRef.current = Date.now() + 1400;
    clearThreadScrollPins();
    for (const ms of [0, 40, 120, 240, 380, 520, 720, 1000, 1400]) {
      threadScrollPinTimers.current.push(window.setTimeout(apply, ms));
    }
  }, []);

  const requestEffectPlay = (messageId: number, effect?: ChatMessageEffect | null) => {
    if (effect !== "impact" && effect !== "echo") return;
    if (revealedEffectsRef.current.has(messageId)) return;
    revealedEffectsRef.current.add(messageId);
    setEffectReplay((prev) => ({
      ...prev,
      [messageId]: (prev[messageId] ?? 0) + 1,
    }));
  };

  const queueEffectReveal = (
    conversationId: number,
    last: ChatMessage | null | undefined,
    unread: number
  ) => {
    if (
      unread > 0 &&
      last &&
      last.sender_id !== selfId &&
      (last.effect === "impact" || last.effect === "echo")
    ) {
      pendingEffectRevealRef.current = {
        conversationId,
        messageId: last.id,
        effect: last.effect,
      };
      return true;
    }
    pendingEffectRevealRef.current = null;
    return false;
  };

  const flushPendingEffectReveal = (delayMs = 160) => {
    const pending = pendingEffectRevealRef.current;
    if (!pending) return;
    if (pending.conversationId !== selectedIdRef.current) return;
    pendingEffectRevealRef.current = null;
    window.setTimeout(() => {
      if (selectedIdRef.current !== pending.conversationId) return;
      if (!isViewingConversation(pending.conversationId)) return;
      requestEffectPlay(pending.messageId, pending.effect);
    }, delayMs);
  };

  const viewColleagues = useMemo(
    () => colleagues.map((c) => applyNickToColleague(c, nicknames)),
    [colleagues, nicknames]
  );
  const viewConversations = useMemo(
    () => conversations.map((c) => applyNickToConversation(c, nicknames)),
    [conversations, nicknames]
  );
  const selected = useMemo(
    () => viewConversations.find((c) => c.id === selectedId) ?? null,
    [viewConversations, selectedId]
  );
  const officialPeerLabel = useCallback(
    (userId: number) => {
      const col = colleagues.find((c) => c.id === userId);
      const mem = conversations
        .flatMap((c) => c.members ?? [])
        .find((m) => m.id === userId);
      return (
        col?.display_name?.trim() ||
        mem?.display_name?.trim() ||
        col?.username ||
        mem?.username ||
        `#${userId}`
      );
    },
    [colleagues, conversations]
  );

  const aurizElig = useMemo(() => aurizEligibility(selected), [selected]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? viewConversations.filter((c) => {
          const shown = conversationTitle(c, selfId).toLowerCase();
          const raw = conversations.find((x) => x.id === c.id);
          const official = raw ? conversationTitle(raw, selfId).toLowerCase() : "";
          return shown.includes(q) || official.includes(q);
        })
      : viewConversations;
    return applyChatSidebarOrder(list, sidebarOrder);
  }, [viewConversations, conversations, search, selfId, sidebarOrder]);

  const typingUsers = selectedId ? typingMap[selectedId] : undefined;
  const typingLabel = useMemo(() => {
    if (!typingUsers || typingUsers.size === 0) return null;
    const names = [...typingUsers]
      .filter((id) => id !== selfId)
      .map((id) => {
        const c = colleagues.find((x) => x.id === id);
        const official = c?.display_name?.trim() || c?.username || `#${id}`;
        return chatNickOrOfficial(id, official, nicknames);
      });
    if (names.length === 0) return null;
    if (names.length === 1) return `${names[0]} écrit…`;
    return `${names.join(", ")} écrivent…`;
  }, [typingUsers, colleagues, selfId, nicknames]);

  const refreshConversations = useCallback(async () => {
    if (!listReadyRef.current) setLoadingList(true);
    const r = await listConversations(suiteUserId);
    if (r.success && r.data) {
      listReadyRef.current = true;
      setConversations(r.data);
      setLoadingList(false);
      if (r.incallUserIds && r.incallUserIds.length > 0) {
        for (const id of r.incallUserIds) overlayIncallRef.current.add(id);
        lastIncallSnapRef.current = Date.now();
        setPresence((prev) => {
          const next = { ...prev };
          let changed = false;
          for (const id of overlayIncallRef.current) {
            if (next[id] !== "incall") {
              next[id] = "incall";
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }
    } else {
      setLoadingList(false);
      setError(r.message || "Impossible de charger les conversations.");
    }
  }, [suiteUserId]);

  useEffect(() => {
    void refreshConversations();
    void listColleagues(suiteUserId).then((r) => {
      if (r.success && r.data) {
        setColleagues(r.data.colleagues);
        setMyClientIds(r.data.clients.map((c) => c.id).filter((id) => id > 0));
        const next: Record<number, string> = {};
        for (const c of r.data.colleagues) {
          next[c.id] = normalizeChatPresence(c.presence);
        }
        setPresence(next);
        const seen: Record<number, number> = {};
        for (const c of r.data.colleagues) {
          if (c.last_seen && c.last_seen > 0) seen[c.id] = c.last_seen;
        }
        mergeLastSeen(seen);
      }
    });
    void fetchChatProfile(suiteUserId).then((r) => {
      if (r.success && r.data) {
        setChatProfile(r.data);
        const remote = parseChatSidebarOrder(r.data.sidebar_order);
        const local = readChatSidebarOrder(selfId);
        if (isChatSidebarOrderEmpty(remote) && !isChatSidebarOrderEmpty(local)) {
          setSidebarOrder(local);
          void saveChatSidebarOrder(suiteUserId, local);
        } else if (!isChatSidebarOrderEmpty(remote)) {
          setSidebarOrder(remote);
          writeChatSidebarOrder(selfId, remote);
        }
        const remoteNicks = parseChatNicknames(r.data.nicknames);
        const localNicks = readChatNicknames(selfId);
        if (isChatNickMapEmpty(remoteNicks) && !isChatNickMapEmpty(localNicks)) {
          setNicknames(localNicks);
          void saveChatNicknames(suiteUserId, localNicks);
        } else if (!isChatNickMapEmpty(remoteNicks)) {
          setNicknames(remoteNicks);
          writeChatNicknames(selfId, remoteNicks);
        }
      }
    });
    void fetchChatPresenceSelf(suiteUserId).then((r) => {
      if (r.success && r.data) {
        setMyStatus(r.data.self === "offline" ? "online" : r.data.self);
        const next: Record<number, string> = {};
        const incall = new Set<number>();
        for (const [k, v] of Object.entries(r.data.presence)) {
          const id = Number(k);
          if (!Number.isFinite(id) || id < 1) continue;
          const n = normalizeChatPresence(v);
          next[id] = n;
          if (n === "incall") incall.add(id);
        }
        if (incall.size > 0) {
          overlayIncallRef.current = incall;
          lastIncallSnapRef.current = Date.now();
        }
        if (Object.keys(next).length > 0) {
          setPresence((prev) => {
            const merged = { ...prev, ...next };
            for (const id of overlayIncallRef.current) merged[id] = "incall";
            return merged;
          });
        }
        mergeLastSeen(r.data.last_seen);
      }
    });
    void syncChatWebPushOnBoot(suiteUserId);
    const onSw = (event: MessageEvent) => {
      if ((event.data as { type?: string } | null)?.type === "CHAT_SW_ACTIVATED") {
        void syncChatWebPushOnBoot(suiteUserId);
      }
    };
    navigator.serviceWorker?.addEventListener("message", onSw);
    return () => navigator.serviceWorker?.removeEventListener("message", onSw);
  }, [suiteUserId, refreshConversations]);

  useEffect(() => {
    if (!suiteUserId) return;
    let cancelled = false;
    const pull = () => {
      void fetchChatPresenceSelf(suiteUserId).then((r) => {
        if (cancelled || !r.success || !r.data) return;
        const next: Record<number, string> = {};
        const incall = new Set<number>();
        for (const [k, v] of Object.entries(r.data.presence)) {
          const id = Number(k);
          if (!Number.isFinite(id) || id < 1) continue;
          const n = normalizeChatPresence(v);
          next[id] = n;
          if (n === "incall") incall.add(id);
        }
        if (incall.size > 0) {
          overlayIncallRef.current = incall;
          lastIncallSnapRef.current = Date.now();
        } else {
          for (const id of overlayIncallRef.current) next[id] = "incall";
        }
        if (Object.keys(next).length > 0) setPresence((prev) => ({ ...prev, ...next }));
        mergeLastSeen(r.data.last_seen);
      });
    };
    const t = window.setInterval(pull, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [suiteUserId]);

  useEffect(() => installChatPushFocusGuard(), []);

  // Deep-link Web Push : ?c= & call= (lire avant de nettoyer l’URL)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get("c");
      const id = raw ? Number(raw) : NaN;
      const callId = (params.get("call") || "").trim();
      const media = params.get("media") === "audio" ? "audio" : "video";
      const fromName = (params.get("from") || "").trim() || "Appel entrant";
      const aurizFlag = params.get("auriz") === "1";
      if (Number.isFinite(id) && id >= 1) {
        pendingOpenConvRef.current = id;
        if (callId) {
          pendingIncomingCallRef.current = {
            conversationId: id,
            callId,
            room: "",
            media,
            fromUserId: 0,
            fromName,
          };
        } else if (aurizFlag) {
          pendingAurizRef.current = {
            fromName: (params.get("from") || "").trim() || "Quelqu’un",
          };
        }
      }
      params.delete("c");
      params.delete("call");
      params.delete("media");
      params.delete("from");
      params.delete("auriz");
      const q = params.toString();
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${q ? `?${q}` : ""}${window.location.hash}`
      );
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (loadingList) return;
    const pendingCall = pendingIncomingCallRef.current;
    if (pendingCall) {
      pendingIncomingCallRef.current = null;
      setIncomingCall(pendingCall);
    }
    const id = pendingOpenConvRef.current;
    if (id == null) return;
    if (!conversations.some((c) => c.id === id)) return;
    pendingOpenConvRef.current = null;
    selectConversation(id);
    const pendingAuriz = pendingAurizRef.current;
    if (pendingAuriz) {
      pendingAurizRef.current = null;
      window.setTimeout(() => playIncomingAuriz(pendingAuriz.fromName), 280);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingList, conversations]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (event: MessageEvent) => {
      if (event.data?.type !== "CHAT_PUSH_OPEN") return;
      const id = Number(event.data.conversation_id);
      const callId = typeof event.data.call_id === "string" ? event.data.call_id : "";
      if (callId) {
        const inv = {
          conversationId: Number.isFinite(id) && id > 0 ? id : 0,
          callId,
          room: typeof event.data.room === "string" ? event.data.room : "",
          media: (event.data.media === "audio" ? "audio" : "video") as "audio" | "video",
          fromUserId: Number(event.data.from_user_id) || 0,
          fromName:
            typeof event.data.from_name === "string" && event.data.from_name
              ? event.data.from_name
              : "Appel entrant",
        };
        if (Number.isFinite(id) && id > 0 && !conversations.some((c) => c.id === id)) {
          pendingOpenConvRef.current = id;
          pendingIncomingCallRef.current = inv;
        } else {
          setIncomingCall(inv);
          if (Number.isFinite(id) && id > 0) selectConversation(id);
        }
        return;
      }
      if (!Number.isFinite(id) || id < 1) return;
      if (event.data.push_type === "auriz") {
        pendingAurizRef.current = {
          fromName:
            typeof event.data.from_name === "string" && event.data.from_name
              ? event.data.from_name
              : "Quelqu’un",
        };
      }
      pendingOpenConvRef.current = id;
      if (!loadingList && conversations.some((c) => c.id === id)) {
        pendingOpenConvRef.current = null;
        selectConversation(id);
        const pendingAuriz = pendingAurizRef.current;
        if (pendingAuriz) {
          pendingAurizRef.current = null;
          window.setTimeout(() => playIncomingAuriz(pendingAuriz.fromName), 280);
        }
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingList, conversations]);

  useEffect(() => {
    if (incomingCall && !callSession) {
      playChatCallRingtone();
      return () => stopChatCallRingtone();
    }
    stopChatCallRingtone();
  }, [incomingCall, callSession]);

  useEffect(() => {
    if (callSession?.role === "caller" && !callAnswered) {
      playChatCallCalling();
      return () => stopChatCallCalling();
    }
    stopChatCallCalling();
  }, [callSession, callAnswered]);

  useEffect(() => {
    return () => stopAllChatCallSounds();
  }, []);

  useEffect(() => {
    if (selected) setRenameDraft(selected.title ?? "");
  }, [selected]);

  useEffect(() => {
    if (selectedId == null) {
      setMessages([]);
      setLoadingThread(false);
      return;
    }
    let cancelled = false;
    const cached = readChatThreadCache(selectedId);
    if (cached && cached.length > 0) {
      setMessages(cached);
      setLoadingThread(false);
    } else {
      setMessages([]);
      setLoadingThread(true);
    }
    setEffectReplay({});
    setReplyTo(null);
    void listMessages(suiteUserId, selectedId).then((r) => {
      if (cancelled) return;
      setLoadingThread(false);
      if (r.success && r.data) {
        writeChatThreadCache(selectedId, r.data);
        setMessages(r.data);
        const last = r.data[r.data.length - 1];
        if (last) {
          const pending = pendingEffectRevealRef.current;
          if (
            pending &&
            pending.conversationId === selectedId &&
            r.data.some((m) => m.id === pending.messageId)
          ) {
            flushPendingEffectReveal(160);
          } else {
            pendingEffectRevealRef.current = null;
          }
          void markRead(suiteUserId, selectedId, last.id);
        }
      } else if (!cached?.length) {
        setError(r.message || "Impossible de charger les messages.");
      }
    });
    return () => {
      cancelled = true;
      clearThreadScrollPins();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reveal helpers stables via refs
  }, [selectedId, suiteUserId, selfId]);

  useEffect(() => {
    setWallpaperPrefs(
      selectedId == null
        ? readChatWallpaper(null)
        : readChatWallpaper(String(selectedId))
    );
  }, [selectedId]);

  // Coller en bas après paint (layout réel), avec retries
  const lastMessageId = messages.length > 0 ? messages[messages.length - 1]!.id : null;
  useLayoutEffect(() => {
    if (selectedId == null) return;
    if (loadingThread && messages.length === 0) return;
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
      if (mobilePane !== "thread") return;
    }
    pinThreadToBottom({ retries: true });
    return () => clearThreadScrollPins();
  }, [lastMessageId, messages.length, typingLabel, loadingThread, selectedId, mobilePane, pinThreadToBottom]);

  useEffect(() => {
    if (selectedId == null || loadingThread) return;
    writeChatThreadCache(selectedId, messages);
  }, [selectedId, messages, loadingThread]);

  // Images / GIF qui chargent après coup → re-pin si on est déjà en bas
  useEffect(() => {
    const scroller = threadScrollRef.current;
    if (!scroller || loadingThread) return;
    const snapIfStuck = () => {
      if (Date.now() > stickBottomUntilRef.current) {
        const gap = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
        if (gap >= 180) return;
      }
      pinThreadToBottom();
    };
    const onAsset = () => snapIfStuck();
    scroller.addEventListener("load", onAsset, true);
    const ro = new ResizeObserver(() => snapIfStuck());
    ro.observe(scroller);
    const inner = scroller.firstElementChild;
    if (inner) ro.observe(inner);
    return () => {
      scroller.removeEventListener("load", onAsset, true);
      ro.disconnect();
    };
  }, [loadingThread, selectedId, mobilePane, pinThreadToBottom]);

  // Rafraîchir les ticks (distribué / lu) sur le fil ouvert
  useEffect(() => {
    if (selectedId == null) return;
    const tick = () => {
      void listMessages(suiteUserId, selectedId).then((r) => {
        if (!r.success || !r.data) return;
        const byId = new Map(r.data.map((m) => [m.id, m]));
        setMessages((prev) => {
          let changed = false;
          const next = prev.map((m) => {
            const fresh = byId.get(m.id);
            if (!fresh) return m;
            if (
              fresh.receipt === m.receipt &&
              fresh.body === m.body &&
              Boolean(fresh.edited) === Boolean(m.edited)
            ) {
              return m;
            }
            changed = true;
            return {
              ...m,
              body: fresh.body,
              edited: fresh.edited,
              receipt: fresh.receipt,
              updated_at: fresh.updated_at,
              poll: (fresh as { poll?: unknown }).poll ?? (m as { poll?: unknown }).poll,
            };
          });
          return changed ? next : prev;
        });
      });
    };
    const id = window.setInterval(tick, 6000);
    return () => window.clearInterval(id);
  }, [selectedId, suiteUserId]);

  useEffect(() => {
    if (selectedId == null) {
      setMeetings([]);
      return;
    }
    const conv = conversations.find((c) => c.id === selectedId);
    if (!conv || conv.type !== "group") {
      setMeetings([]);
      return;
    }
    let cancelled = false;
    void listChatMeetings(suiteUserId, selectedId).then((r) => {
      if (!cancelled && r.success && r.data) setMeetings(r.data);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, suiteUserId]);

  useChatRealtime(suiteUserId, true, {
    onMessage: (msg) => {
      const viewing = isViewingConversation(msg.conversation_id);
      if (msg.conversation_id === selectedId) {
        const already = messagesRef.current.some((m) => m.id === msg.id);
        if (!already) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === msg.id)) return prev;
            if (msg.sender_id === selfId) {
              const pend = prev.find(
                (m) => m.pending && m.sender_id === selfId && m.conversation_id === msg.conversation_id
              );
              if (pend) {
                for (const a of pend.attachments ?? []) {
                  if (a.url.startsWith("blob:")) URL.revokeObjectURL(a.url);
                }
                return prev.map((m) => (m.id === pend.id ? msg : m));
              }
            }
            return [...prev, msg];
          });
          // Effet uniquement si le fil est vraiment ouvert (pas depuis la liste)
          if (viewing) {
            requestEffectPlay(msg.id, msg.effect);
          }
        }
      }
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === msg.conversation_id);
        if (idx < 0) {
          void refreshConversations();
          return prev;
        }
        const next = [...prev];
        const cur = next[idx]!;
        const unread =
          viewing || msg.sender_id === selfId
            ? cur.unread_count ?? 0
            : (cur.unread_count ?? 0) + 1;
        next[idx] = {
          ...cur,
          last_message: msg,
          unread_count: unread,
          updated_at: msg.created_at,
          last_activity_label: undefined,
          last_interact: undefined,
        };
        return next;
      });
      if (msg.sender_id !== selfId) {
        playChatNotificationSound();
      }
      if (viewing && msg.sender_id !== selfId) {
        void markRead(suiteUserId, msg.conversation_id, msg.id);
      }
    },
    onTyping: (conversationId, userId, typing) => {
      setTypingMap((prev) => {
        const set = new Set(prev[conversationId] ?? []);
        if (typing) set.add(userId);
        else set.delete(userId);
        return { ...prev, [conversationId]: set };
      });
    },
    onPresenceSnapshot: (snap, seen) => {
      const now = Date.now();
      setPresence((prev) => {
        if (Object.keys(snap).length === 0 && Object.keys(prev).length > 0) return prev;
        const incomingIncall = Object.entries(snap)
          .filter(([, v]) => v === "incall")
          .map(([k]) => Number(k));
        const next: Record<number, string> = { ...snap };
        if (incomingIncall.length > 0) {
          lastIncallSnapRef.current = now;
          for (const id of incomingIncall) overlayIncallRef.current.add(id);
        } else if (!(lastIncallSnapRef.current > 0 && now - lastIncallSnapRef.current > 90_000)) {
          // Snapshot sans incall : ne pas casser un appel en cours
          for (const [k, v] of Object.entries(prev)) {
            if (v === "incall") next[Number(k)] = "incall";
          }
        }
        for (const id of overlayIncallRef.current) next[id] = "incall";
        return next;
      });
      if (seen) mergeLastSeen(seen);
    },
    onRead: (conversationId, userId, messageId) => {
      if (userId === selfId) {
        // Multi-appareil : lecture sur mobile/autre session → synchroniser les badges ici
        setConversations((prev) =>
          prev.map((c) =>
            c.id === conversationId ? { ...c, unread_count: 0 } : c
          )
        );
        return;
      }
      // Lecture par le destinataire → V « lu » sur nos messages
      setMessages((prev) =>
        prev.map((m) => {
          if (m.conversation_id !== conversationId || m.sender_id !== selfId) return m;
          if (m.id > messageId) return m;
          const cur = m.receipt ?? "sent";
          if (cur === "read") return m;
          return { ...m, receipt: "read" as ChatReceipt };
        })
      );
    },
    onMessageEdited: (msg) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, ...msg } : m))
      );
      setConversations((prev) =>
        prev.map((c) => {
          if (c.id !== msg.conversation_id && c.last_message?.id !== msg.id) return c;
          const prevSrc =
            c.last_message?.id === msg.id
              ? c.last_message
              : messagesRef.current.find((m) => m.id === msg.id);
          const prevVoter = messagePoll(prevSrc)?.last_voter_id ?? null;
          const nextPoll = messagePoll(msg);
          const nextVoter = nextPoll?.last_voter_id ?? null;
          const voteFromOther =
            typeof nextVoter === "number" &&
            nextVoter > 0 &&
            nextVoter !== selfId &&
            nextVoter !== prevVoter;
          const viewing = isViewingConversation(msg.conversation_id);
          let unread = c.unread_count ?? 0;
          if (voteFromOther && !viewing && c.id === msg.conversation_id) {
            unread += 1;
          }
          const actorOfficial = voteFromOther
            ? (c.members?.find((m) => m.id === nextVoter)?.display_name?.trim() ||
                c.members?.find((m) => m.id === nextVoter)?.username ||
                colleagues.find((x) => x.id === nextVoter)?.display_name?.trim() ||
                colleagues.find((x) => x.id === nextVoter)?.username ||
                "Quelqu’un")
            : "";
          const actorName = voteFromOther
            ? chatNickOrOfficial(nextVoter, actorOfficial, nicknamesRef.current).split(/\s+/)[0]
            : "";
          return {
            ...c,
            last_message:
              c.last_message?.id === msg.id || (voteFromOther && c.id === msg.conversation_id)
                ? { ...(c.last_message ?? msg), ...msg }
                : c.last_message,
            unread_count: unread,
            updated_at: voteFromOther ? msg.updated_at ?? c.updated_at : c.updated_at,
            last_activity_label: voteFromOther && actorName ? `${actorName} a voté` : c.last_activity_label,
          };
        })
      );
      if (isViewingConversation(msg.conversation_id) && messagePoll(msg)?.last_voter_id === selfId) {
        /* son propre vote */
      } else if (isViewingConversation(msg.conversation_id) && (messagePoll(msg)?.last_voter_id ?? 0) > 0) {
        void markRead(suiteUserId, msg.conversation_id, msg.id);
      }
    },
    onMessageDeleted: (conversationId, messageId) => {
      setMessages((prev) =>
        prev.filter((m) => !(m.conversation_id === conversationId && m.id === messageId))
      );
      void refreshConversations();
    },
    onMessageReaction: (conversationId, messageId, reactions, meta) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.conversation_id === conversationId && m.id === messageId
            ? { ...m, reactions }
            : m
        )
      );
      const fromOther =
        Boolean(meta) &&
        (meta?.userId ?? 0) > 0 &&
        meta!.userId !== selfId &&
        (meta!.action === "added" || meta!.action === "changed");
      if (fromOther && meta?.emoji) {
        playChatEmojiPop();
        spawnLocalBurst(meta.emoji, messageId);
      }
      if (!fromOther || !meta) return;
      const viewing = isViewingConversation(conversationId);
      const actorOfficial = (
        colleagues.find((x) => x.id === meta.userId)?.display_name?.trim() ||
        colleagues.find((x) => x.id === meta.userId)?.username ||
        "Quelqu’un"
      );
      const actorName = chatNickOrOfficial(meta.userId, actorOfficial, nicknamesRef.current).split(/\s+/)[0];
      const emoji = (meta.emoji || "").trim();
      setConversations((prev) =>
        prev.map((c) => {
          if (c.id !== conversationId) return c;
          return {
            ...c,
            unread_count: viewing ? c.unread_count ?? 0 : (c.unread_count ?? 0) + 1,
            last_activity_label: `${actorName} a réagi${emoji ? ` ${emoji}` : ""}`,
            last_interact: { kind: "reaction", user_id: meta.userId, emoji: meta.emoji },
          };
        })
      );
      if (viewing) {
        void markRead(suiteUserId, conversationId, messageId);
      }
    },
    onProfileUpdated: (profile) => {
      const uid = Number(profile.user_id);
      if (!Number.isFinite(uid) || uid < 1) return;
      if (uid === selfId) {
        setChatProfile(profile);
        if (profile.sidebar_order) {
          const next = parseChatSidebarOrder(profile.sidebar_order);
          setSidebarOrder(next);
          writeChatSidebarOrder(selfId, next);
        }
        if (profile.nicknames) {
          const next = parseChatNicknames(profile.nicknames);
          setNicknames(next);
          writeChatNicknames(selfId, next);
        }
      }
      setColleagues((prev) =>
        prev.map((c) =>
          c.id === uid
            ? {
                ...c,
                avatar_url: profile.avatar_url,
                display_name: profile.display_name ?? c.display_name,
                username: profile.username || c.username,
              }
            : c
        )
      );
      setConversations((prev) =>
        prev.map((conv) => {
          if (!conv.members?.some((m) => m.id === uid)) return conv;
          return {
            ...conv,
            members: conv.members.map((m) =>
              m.id === uid
                ? {
                    ...m,
                    avatar_url: profile.avatar_url,
                    display_name: profile.display_name ?? m.display_name,
                    username: profile.username || m.username,
                  }
                : m
            ),
          };
        })
      );
    },
    onConversationUpdated: (conv) => {
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === conv.id);
        if (idx < 0) return [conv, ...prev];
        const next = [...prev];
        // Fusionner sans écraser un unread local plus récent si le payload n’en a pas
        const cur = next[idx]!;
        next[idx] = {
          ...cur,
          ...conv,
          unread_count:
            typeof conv.unread_count === "number"
              ? conv.unread_count
              : cur.unread_count,
        };
        return next;
      });
    },
    onConversationRemoved: (conversationId) => {
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));
      if (selectedId === conversationId) {
        setSelectedId(null);
        setMobilePane("list");
        setShowInfo(false);
      }
    },
    onCallInvite: (payload) => {
      markUsersInCall([payload.fromUserId]);
      const targeted = payload.memberIds && payload.memberIds.length > 0;
      const invited =
        !targeted ||
        payload.memberIds!.includes(selfId) ||
        payload.fromUserId === selfId;
      setConversations((prev) =>
        prev.map((c) =>
          c.id === payload.conversationId
            ? {
                ...c,
                active_call: {
                  call_id: payload.callId,
                  room: payload.room,
                  media: payload.media,
                  status: "active",
                  created_by: payload.fromUserId,
                  joinable: invited,
                },
              }
            : c
        )
      );
      if (!invited) return;
      if (payload.fromUserId === selfId) return;
      const cur = callSessionRef.current;
      if (cur) return; // déjà en appel
      setIncomingCall(payload);
      playChatNotificationSound();
    },
    onCallAccept: (_cid, callId, userId) => {
      markUsersInCall([userId]);
      clearRingTimer();
      setCallAnswered(true);
      // Décrochage côté appelant (WS) — Open après cut calling
      playChatCallOpen();
      void callId;
    },
    onCallReject: (_cid, callId) => {
      clearRingTimer();
      setIncomingCall((prev) => (prev?.callId === callId ? null : prev));
      const cur = callSessionRef.current;
      if (cur?.callId === callId) {
        playChatCallClose();
        setCallSession(null);
        setCallAnswered(false);
      } else {
        stopAllChatCallSounds();
      }
    },
    onCallEnd: (cid, callId) => {
      clearRingTimer();
      setIncomingCall((prev) => (prev?.callId === callId ? null : prev));
      setConversations((prev) =>
        prev.map((c) =>
          c.id === cid && c.active_call?.call_id === callId
            ? { ...c, active_call: null }
            : c
        )
      );
      const conv = conversationsRef.current.find((c) => c.id === cid);
      if (conv) {
        for (const id of conv.member_ids) overlayIncallRef.current.delete(Number(id));
        setPresence((prev) => {
          let changed = false;
          const next = { ...prev };
          for (const id of conv.member_ids) {
            const uid = Number(id);
            if (next[uid] === "incall" && !overlayIncallRef.current.has(uid)) {
              next[uid] = "online";
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      }
      const cur = callSessionRef.current;
      if (cur?.callId === callId) {
        playChatCallClose();
        setCallSession(null);
        setCallAnswered(false);
      } else {
        stopAllChatCallSounds();
      }
    },
    onCallJoin: (cid, callId, userId) => {
      markUsersInCall([userId]);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === cid
            ? {
                ...c,
                active_call: {
                  call_id: callId,
                  room: c.active_call?.room || "",
                  media: c.active_call?.media || "video",
                  status: "active",
                  created_by: c.active_call?.created_by,
                  joinable: c.active_call?.joinable,
                },
              }
            : c
        )
      );
    },
    onCallStarted: (payload) => {
      markUsersInCall([payload.fromUserId]);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === payload.conversationId
            ? {
                ...c,
                active_call: {
                  call_id: payload.callId,
                  room: payload.room,
                  media: payload.media,
                  status: "active",
                  created_by: payload.fromUserId,
                  joinable: payload.joinable,
                },
              }
            : c
        )
      );
      // Pas de bannière sonnerie : juste Rejoindre
    },
    onMeetingChanged: (cid) => {
      if (cid === selectedIdRef.current) {
        void listChatMeetings(suiteUserId, cid).then((r) => {
          if (r.success && r.data) setMeetings(r.data);
        });
      }
    },
    onAuriz: (payload) => {
      if (payload.fromUserId === selfId) return;
      playIncomingAuriz(payload.fromName);
    },
  });

  const selectConversation = (id: number) => {
    const sameId = id === selectedId;
    setSelectedId(id);
    setMobilePane("thread");
    setShowInfo(false);
    setEditingMsgId(null);
    setShowPicker(false);
    setWallpaperPrefs(readChatWallpaper(String(id)));
    const conv = conversations.find((c) => c.id === id);
    const last = conv?.last_message;
    const unread = conv?.unread_count ?? 0;
    queueEffectReveal(id, last, unread);
    const lastId = last?.id;
    setConversations((prev) =>
      prev.map((c) => (c.id === id ? { ...c, unread_count: 0, last_activity_label: undefined } : c))
    );
    if (lastId != null && lastId > 0) {
      void markRead(suiteUserId, id, lastId);
    }
    // Même conversation depuis la liste : pas de reload → révéler tout de suite
    if (sameId) {
      flushPendingEffectReveal(120);
    }
    void fetchChatPresenceSelf(suiteUserId).then((r) => {
      if (!r.success || !r.data) return;
      const next: Record<number, string> = {};
      const incall = new Set<number>();
      for (const [k, v] of Object.entries(r.data.presence)) {
        const uid = Number(k);
        if (!Number.isFinite(uid) || uid < 1) continue;
        const n = normalizeChatPresence(v);
        next[uid] = n;
        if (n === "incall") incall.add(uid);
      }
      if (incall.size > 0) {
        overlayIncallRef.current = incall;
        lastIncallSnapRef.current = Date.now();
      } else {
        for (const uid of overlayIncallRef.current) next[uid] = "incall";
      }
      if (Object.keys(next).length > 0) setPresence((prev) => ({ ...prev, ...next }));
      mergeLastSeen(r.data.last_seen);
    });
  };

  /** Quitte le fil : liste mobile, ou écran d’accueil desktop. */
  const leaveToSalon = () => {
    setShowInfo(false);
    setShowPicker(false);
    setEditingMsgId(null);
    setReplyTo(null);
    setSendEffectsOpen(false);
    setThreadFileDragOver(false);
    threadDragDepthRef.current = 0;
    notifyTyping(false);
    const mobile =
      typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
    if (mobile) {
      goBackMobile();
      return;
    }
    setSelectedId(null);
    setMobilePane("list");
  };

  const openMediaLightbox = (src: string, alt?: string, attachment?: ChatAttachment) => {
    if (Date.now() < suppressMediaClickUntil.current) return;
    setLightbox({ src, alt, attachment });
  };

  const reportSave = (ok: boolean, okMsg: string, err?: string) => {
    setError(ok ? okMsg : err || "Action impossible.");
  };

  const runDownloadAtts = async (atts: ChatAttachment[]) => {
    if (atts.length === 0) return;
    if (atts.length === 1) {
      const ready = cachedChatFile(atts[0]);
      if (ready) {
        shareOrDownloadFile(ready);
        return;
      }
    }
    setSaveBusy(true);
    const r = atts.length === 1 ? await downloadChatAttachment(atts[0]) : await downloadChatAttachments(atts);
    setSaveBusy(false);
    reportSave(r.ok, atts.length > 1 ? "Fichiers téléchargés." : "Fichier téléchargé.", r.error);
  };

  const armMenuAction = () => {
    suppressMediaClickUntil.current = Date.now() + 500;
  };

  const openAvatarPreview = (url: string | null | undefined, alt?: string) => {
    if (!url) return;
    const src = resolveChatAvatarUrl(url) || url;
    setLightbox({ src, alt: alt || "Photo de profil", variant: "avatar" });
  };

  const replyAuthorLabel = (m: ChatMessage) => {
    if (m.sender_id === selfId) return "Vous";
    return (
      m.sender_name?.trim() ||
      memberLabel(
        colleagues.find((c) => c.id === m.sender_id),
        `#${m.sender_id}`
      )
    );
  };

  const beginReply = (m: ChatMessage) => {
    armMenuAction();
    setReplyTo(m);
    setCtxMenu(null);
    setEditingMsgId(null);
    requestAnimationFrame(() => {
      draftRef.current?.focus();
    });
  };

  const resolveReplyPreview = (m: ChatMessage) => {
    if (m.reply_to) return m.reply_to;
    if (m.reply_to_id == null) return null;
    const src = messages.find((x) => x.id === m.reply_to_id);
    if (!src) return null;
    return {
      id: src.id,
      body: src.body,
      sender_id: src.sender_id,
      sender_name: src.sender_name ?? null,
    };
  };

  const scrollToMessage = (messageId: number) => {
    const el = document.querySelector(`[data-chat-msg-id="${messageId}"]`) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Relance l’anim même si on retape la même citation
    setFlashReplyId(null);
    window.requestAnimationFrame(() => {
      setFlashReplyId(messageId);
      window.setTimeout(() => setFlashReplyId((cur) => (cur === messageId ? null : cur)), 1450);
    });
  };

  const handleWallpaperChange = (next: ChatWallpaperPrefs) => {
    if (!selected) {
      setWallpaperPrefs(next);
      return;
    }
    try {
      setWallpaperPrefs(persistChatWallpaper(String(selected.id), next));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Impossible d’enregistrer le fond.");
    }
  };

  const addComposerFiles = useCallback((list: File[]) => {
    if (!list.length) return;
    const accepted: File[] = [];
    for (const f of list) {
      const max = chatFileMaxBytes(f);
      if (f.size > max) {
        setError(
          chatUploadTooLargeMessage(
            f.name,
            isChatVideoFile(f) ? CHAT_MAX_VIDEO_UPLOAD_LABEL : undefined
          )
        );
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
  }, []);

  const onThreadDragEnter = useCallback((e: DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    threadDragDepthRef.current += 1;
    setThreadFileDragOver(true);
  }, []);

  const onThreadDragOver = useCallback((e: DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const onThreadDragLeave = useCallback((e: DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    threadDragDepthRef.current = Math.max(0, threadDragDepthRef.current - 1);
    if (threadDragDepthRef.current === 0) setThreadFileDragOver(false);
  }, []);

  const onThreadDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      threadDragDepthRef.current = 0;
      setThreadFileDragOver(false);
      const list = e.dataTransfer?.files ? [...e.dataTransfer.files] : [];
      addComposerFiles(list);
    },
    [addComposerFiles]
  );

  const notifyTyping = (active: boolean) => {
    if (selectedId == null) return;
    if (active === typingSent.current) return;
    typingSent.current = active;
    void setTyping(suiteUserId, selectedId, active);
  };

  const onDraftChange = (value: string, caret = value.length) => {
    const expanded = expandEmoticonsWithCaret(value, caret);
    setDraft(expanded.text);
    requestAnimationFrame(() => {
      const el = draftRef.current;
      if (!el) return;
      const pos = expanded.caret;
      el.setSelectionRange(pos, pos);
      el.style.height = "auto";
      el.style.height = `${Math.min(Math.max(el.scrollHeight, 40), 128)}px`;
    });
    if (selectedId == null) return;
    notifyTyping(true);
    if (typingTimer.current) window.clearTimeout(typingTimer.current);
    typingTimer.current = window.setTimeout(() => notifyTyping(false), 1800);
  };

  const openSendEffectsMenu = () => {
    if (!draft.trim()) {
      setError("Les effets d’envoi nécessitent un message texte.");
      return;
    }
    setShowPicker(false);
    // Fermer le clavier d’abord, sinon le menu saute quand la viewport se recalcule
    draftRef.current?.blur();
    const open = () => setSendEffectsOpen(true);
    const vv = window.visualViewport;
    const keyboardOpen =
      !!vv && vv.height < window.innerHeight - 120;
    if (!keyboardOpen) {
      open();
      return;
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      vv?.removeEventListener("resize", onResize);
      window.clearTimeout(fallback);
      // petit délai après collapse clavier
      window.setTimeout(open, 60);
    };
    const onResize = () => finish();
    vv?.addEventListener("resize", onResize);
    const fallback = window.setTimeout(finish, 380);
  };

  const handleSend = async (effect?: ChatMessageEffect | null) => {
    if (selectedId == null) return;
    const body = expandEmoticons(draft.trim());
    if (!body && files.length === 0) return;
    if (effect && !body) {
      setError("Les effets d’envoi nécessitent un message texte.");
      return;
    }
    setDraft("");
    if (draftRef.current) {
      draftRef.current.style.height = "auto";
    }
    const pendingFiles = files;
    setFiles([]);
    setShowPicker(false);
    setSendEffectsOpen(false);
    const replyId = replyTo?.id;
    setReplyTo(null);
    notifyTyping(false);
    if (effect === "impact") primeChatImpact();
    else if (effect === "echo") primeChatEcho();
    else playChatSendPop();
    const localId = -Date.now();
    const blobAtts = pendingFiles.map((f) => ({
      url: URL.createObjectURL(f),
      name: f.name,
      mime: f.type,
      size: f.size,
    }));
    const cited = replyId != null ? messages.find((m) => m.id === replyId) : null;
    setMessages((prev) => [
      ...prev,
      {
        id: localId,
        conversation_id: selectedId,
        sender_id: selfId,
        body,
        created_at: new Date().toISOString(),
        reply_to_id: replyId,
        reply_to: cited
          ? {
              id: cited.id,
              body: cited.body,
              sender_id: cited.sender_id,
              sender_name: cited.sender_name,
            }
          : null,
        attachments: blobAtts,
        effect: effect ?? undefined,
        pending: true,
      },
    ]);
    const r =
      pendingFiles.length > 0
        ? await sendMessageWithFiles(suiteUserId, {
            conversation_id: selectedId,
            body,
            files: pendingFiles,
            effect: effect ?? undefined,
            reply_to_id: replyId,
          })
        : await sendMessage(suiteUserId, {
            conversation_id: selectedId,
            body,
            effect: effect ?? undefined,
            reply_to_id: replyId,
          });
    const dropBlobs = () => {
      for (const a of blobAtts) {
        if (a.url.startsWith("blob:")) URL.revokeObjectURL(a.url);
      }
    };
    if (r.success && r.data) {
      setMessages((prev) => {
        if (prev.some((m) => m.id === r.data!.id)) {
          return prev.filter((m) => m.id !== localId);
        }
        return prev.map((m) => (m.id === localId ? r.data! : m));
      });
      dropBlobs();
      requestEffectPlay(r.data.id, r.data.effect);
      void refreshConversations();
    } else {
      dropBlobs();
      setMessages((prev) => prev.filter((m) => m.id !== localId));
      if (effect === "impact" || effect === "echo") endImpactWindup();
      setError(r.message || "Envoi impossible.");
      setDraft(body);
      setFiles(pendingFiles);
      if (replyId != null) {
        const restored = messages.find((m) => m.id === replyId) ?? null;
        setReplyTo(restored);
      }
    }
  };

  const insertEmoji = (emoji: string) => {
    playChatEmojiPop();
    setDraft((prev) => {
      const next = `${prev}${emoji}`;
      if (selectedId != null) {
        notifyTyping(true);
        if (typingTimer.current) window.clearTimeout(typingTimer.current);
        typingTimer.current = window.setTimeout(() => notifyTyping(false), 1800);
      }
      return next;
    });
    requestAnimationFrame(() => {
      const el = draftRef.current;
      if (!el) return;
      el.focus();
      el.style.height = "auto";
      el.style.height = `${Math.min(Math.max(el.scrollHeight, 40), 128)}px`;
    });
  };

  const handleSendCapturedPhoto = async (file: File, caption: string): Promise<boolean> => {
    if (selectedId == null) return false;
    if (file.size > chatFileMaxBytes(file)) {
      setError(
        chatUploadTooLargeMessage(
          file.name,
          isChatVideoFile(file) ? CHAT_MAX_VIDEO_UPLOAD_LABEL : undefined
        )
      );
      return false;
    }
    const body = expandEmoticons(caption.trim());
    notifyTyping(false);
    playChatSendPop();
    const replyId = replyTo?.id;
    setReplyTo(null);
    const localId = -Date.now();
    const blobUrl = URL.createObjectURL(file);
    const convId = selectedId;
    setMessages((prev) => [
      ...prev,
      {
        id: localId,
        conversation_id: convId,
        sender_id: selfId,
        body,
        created_at: new Date().toISOString(),
        reply_to_id: replyId,
        attachments: [{ url: blobUrl, name: file.name, mime: file.type, size: file.size }],
        pending: true,
      },
    ]);
    void sendMessageWithFiles(suiteUserId, {
      conversation_id: convId,
      body,
      files: [file],
      reply_to_id: replyId,
    }).then((r) => {
      if (blobUrl.startsWith("blob:")) URL.revokeObjectURL(blobUrl);
      if (r.success && r.data) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === r.data!.id)) return prev.filter((m) => m.id !== localId);
          return prev.map((m) => (m.id === localId ? r.data! : m));
        });
        void refreshConversations();
        return;
      }
      setMessages((prev) => prev.filter((m) => m.id !== localId));
      setError(r.message || "Envoi de la photo impossible.");
      addComposerFiles([file]);
      if (body) setDraft((prev) => prev || body);
      if (replyId != null) {
        const restored = messages.find((m) => m.id === replyId) ?? null;
        setReplyTo(restored);
      }
    });
    return true;
  };

  const handleSendVoice = async (file: File) => {
    if (selectedId == null) return;
    notifyTyping(false);
    playChatSendPop();
    const replyId = replyTo?.id;
    setReplyTo(null);
    const localId = -Date.now();
    const blobUrl = URL.createObjectURL(file);
    setMessages((prev) => [
      ...prev,
      {
        id: localId,
        conversation_id: selectedId,
        sender_id: selfId,
        body: "",
        created_at: new Date().toISOString(),
        reply_to_id: replyId,
        attachments: [{ url: blobUrl, name: file.name, mime: file.type, size: file.size }],
        pending: true,
      },
    ]);
    const r = await sendMessageWithFiles(suiteUserId, {
      conversation_id: selectedId,
      body: "",
      files: [file],
      reply_to_id: replyId,
    });
    if (blobUrl.startsWith("blob:")) URL.revokeObjectURL(blobUrl);
    if (r.success && r.data) {
      setMessages((prev) => {
        if (prev.some((m) => m.id === r.data!.id)) return prev.filter((m) => m.id !== localId);
        return prev.map((m) => (m.id === localId ? r.data! : m));
      });
      void refreshConversations();
    } else {
      setMessages((prev) => prev.filter((m) => m.id !== localId));
      setError(r.message || "Envoi du vocal impossible.");
      if (replyId != null) {
        const restored = messages.find((m) => m.id === replyId) ?? null;
        setReplyTo(restored);
      }
    }
  };

  const handleSendGif = async (gif: ChatGifItem) => {
    if (selectedId == null) return;
    setShowPicker(false);
    notifyTyping(false);
    playChatSendPop();
    const replyId = replyTo?.id;
    setReplyTo(null);
    const localId = -Date.now();
    setMessages((prev) => [
      ...prev,
      {
        id: localId,
        conversation_id: selectedId,
        sender_id: selfId,
        body: gif.url,
        created_at: new Date().toISOString(),
        reply_to_id: replyId,
        pending: true,
      },
    ]);
    const r = await sendMessage(suiteUserId, {
      conversation_id: selectedId,
      body: gif.url,
      reply_to_id: replyId,
    });
    if (r.success && r.data) {
      setMessages((prev) => {
        if (prev.some((m) => m.id === r.data!.id)) return prev.filter((m) => m.id !== localId);
        return prev.map((m) => (m.id === localId ? r.data! : m));
      });
      void refreshConversations();
    } else {
      setMessages((prev) => prev.filter((m) => m.id !== localId));
      setError(r.message || "Envoi du GIF impossible.");
      if (replyId != null) {
        const restored = messages.find((m) => m.id === replyId) ?? null;
        setReplyTo(restored);
      }
    }
  };

  const startDm = async (userId: number) => {
    const existing = conversations.find(
      (c) => c.type === "dm" && c.member_ids.includes(userId) && c.member_ids.includes(selfId)
    );
    if (existing) {
      selectConversation(existing.id);
      setShowNewDm(false);
      return;
    }
    const r = await createConversation(suiteUserId, { type: "dm", member_ids: [userId] });
    if (r.success && r.data) {
      await refreshConversations();
      selectConversation(r.data.id);
      setShowNewDm(false);
    } else {
      setError(
        r.error === "no_common_client"
          ? "Pas de client Chat en commun avec cette personne."
          : r.message || "Création du message privé impossible."
      );
    }
  };

  const startGroup = async () => {
    if (groupAsVisioRoom && groupMembers.length > 3) {
      setError("Salle de visio : maximum 3 invités (4 personnes au total).");
      return;
    }
    const r = await createConversation(suiteUserId, {
      type: "group",
      member_ids: groupMembers,
      title: groupTitle.trim() || (groupAsVisioRoom ? "Salle de visio" : undefined),
      is_room: groupAsVisioRoom,
    });
    if (r.success && r.data) {
      await refreshConversations();
      selectConversation(r.data.id);
      setShowNewGroup(false);
      setGroupTitle("");
      setGroupMembers([]);
      setGroupAsVisioRoom(false);
    } else {
      setError(
        r.error === "no_common_client"
          ? "Pas de client Chat en commun entre les membres sélectionnés."
          : r.message || "Création du groupe impossible."
      );
    }
  };

  const startGuestFromPlus = async (payload: {
    email: string;
    mode: "now" | "later";
    startsAt: string;
    duration: number;
    remindMinutes: number;
  }) => {
    if (payload.mode === "now" && callSession) {
      setError("Terminez la visio en cours avant d’en lancer une autre.");
      throw new Error("Visio déjà en cours");
    }
    const conv = await createConversation(suiteUserId, {
      type: "group",
      member_ids: [selfId],
      title: "Visio invitée",
      is_room: true,
    });
    if (!conv.success || !conv.data) {
      setError(conv.message || "Impossible de créer la salle.");
      throw new Error(conv.message || "salle");
    }
    const convId = conv.data.id;
    await refreshConversations();
    selectConversation(convId);
    if (payload.mode === "later") {
      const meet = await scheduleChatMeeting(
        suiteUserId,
        convId,
        payload.startsAt,
        payload.duration,
        "Visio invitée",
        payload.remindMinutes
      );
      if (!meet.success || !meet.data) {
        setError(meet.message || "Impossible de planifier.");
        throw new Error(meet.message || "planif");
      }
      const g = await createChatGuestInvite(suiteUserId, {
        conversationId: convId,
        meetingId: meet.data.meeting.id,
        email: payload.email,
        startsAt: meet.data.meeting.starts_at || payload.startsAt,
      });
      if (!g.success || !g.data) {
        setError(g.message || "Impossible d’inviter.");
        throw new Error(g.message || "invite");
      }
      await copyText(g.data.join_url);
      setShowNewGuest(false);
      return;
    }
    const callId = await startCallForConversation(convId, "video", payload.email);
    if (!callId) {
      throw new Error("Impossible de démarrer la visio.");
    }
    const g = await createChatGuestInvite(suiteUserId, {
      conversationId: convId,
      callId,
      email: payload.email,
    });
    if (!g.success || !g.data) {
      setError(g.message || "Visio ouverte, mais invitation impossible.");
      throw new Error(g.message || "invite");
    }
    await copyText(g.data.join_url);
    setShowNewGuest(false);
  };

  const handleCreatePoll = async (question: string, options: string[]) => {
    if (selectedId == null) return;
    const r = await createChatPoll(suiteUserId, selectedId, question, options);
    if (!r.success) {
      setError(r.message);
      return;
    }
    setMessages((prev) => (prev.some((m) => m.id === r.message.id) ? prev : [...prev, r.message]));
    setConversations((prev) => {
      const idx = prev.findIndex((c) => c.id === selectedId);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx]!, last_message: r.message, updated_at: r.message.created_at };
      return next;
    });
  };

  const applyPollMessage = (msg: ChatMessage) => {
    setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, ...msg } : m)));
    setConversations((prev) =>
      prev.map((c) =>
        c.last_message?.id === msg.id ? { ...c, last_message: { ...c.last_message, ...msg } } : c
      )
    );
  };

  const handleVotePoll = async (messageId: number, optionId: number) => {
    const r = await voteChatPoll(suiteUserId, messageId, optionId);
    if (!r.success) {
      setError(r.message);
      return;
    }
    applyPollMessage(r.message);
  };

  const handleClosePoll = async (messageId: number) => {
    const r = await closeChatPoll(suiteUserId, messageId);
    if (!r.success) {
      setError(r.message);
      return;
    }
    applyPollMessage(r.message);
  };

  const openMsgMenu = (m: ChatMessage, x: number, y: number, sheet = false) => {
    longPressOpenedAt.current = Date.now();
    playChatLongPressPop();
    setCtxMenu({ x, y, message: m, sheet });
  };

  const clearLongPress = () => {
    if (longPressTimer.current) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressOrigin.current = null;
  };

  const onBubblePointerDown = (m: ChatMessage, e: React.PointerEvent) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // Souris : clic droit géré par onContextMenu
    if (e.pointerType === "mouse") return;
    const x = e.clientX;
    const y = e.clientY;
    clearLongPress();
    longPressOrigin.current = { x, y };
    unlockChatLongPressPop();
    longPressTimer.current = window.setTimeout(() => {
      longPressTimer.current = null;
      longPressOrigin.current = null;
      try {
        if (typeof navigator !== "undefined" && "vibrate" in navigator) {
          navigator.vibrate(12);
        }
      } catch {
        // ignore
      }
      openMsgMenu(m, x, y, true);
    }, 420);
  };

  const onBubblePointerMove = (e: React.PointerEvent) => {
    const origin = longPressOrigin.current;
    if (!origin || longPressTimer.current == null) return;
    const dx = e.clientX - origin.x;
    const dy = e.clientY - origin.y;
    // Tolérance : le doigt bouge toujours un peu
    if (dx * dx + dy * dy > 14 * 14) {
      clearLongPress();
    }
  };

  const handleRenameConversation = async () => {
    if (!selected || selected.type !== "group") return;
    const title = renameDraft.trim();
    if (!title) return;
    const r = await renameConversation(suiteUserId, selected.id, title);
    if (r.success && r.data) {
      setConversations((prev) =>
        prev.map((c) => (c.id === selected.id ? { ...c, ...r.data } : c))
      );
    } else {
      setError(r.message || "Renommage impossible.");
    }
  };

  const handleGroupAvatar = async (file: File) => {
    if (!selected || selected.type !== "group") return;
    if (file.size > CHAT_MAX_UPLOAD_BYTES) {
      setError(chatUploadTooLargeMessage(file.name));
      return;
    }
    const r = await updateConversation(suiteUserId, {
      id: selected.id,
      title: renameDraft.trim() || selected.title || undefined,
      avatar: file,
    });
    if (r.success && r.data) {
      setConversations((prev) =>
        prev.map((c) => (c.id === selected.id ? { ...c, ...r.data } : c))
      );
    } else {
      setError(r.message || "Photo de groupe impossible.");
    }
  };

  const handleTogglePin = useCallback(
    (id: number) => {
      setSidebarOrder((prev) => {
        const next = toggleChatSidebarPin(prev, id);
        writeChatSidebarOrder(selfId, next);
        void saveChatSidebarOrder(suiteUserId, next);
        return next;
      });
    },
    [selfId, suiteUserId]
  );

  const handleSaveNickname = useCallback(
    (userId: number, name: string | null) => {
      if (userId === selfId) return;
      setNicknames((prev) => {
        const next = setChatNickname(prev, userId, name);
        writeChatNicknames(selfId, next);
        void saveChatNicknames(suiteUserId, next);
        return next;
      });
    },
    [selfId, suiteUserId]
  );

  const deleteModeFor = (c: ChatConversation): "leave" | "delete" => {
    const isGroup = c.type === "group";
    const isOwner = c.created_by === selfId;
    return isGroup && isOwner ? "delete" : "leave";
  };

  const deleteCopyFor = (c: ChatConversation) => {
    const mode = deleteModeFor(c);
    if (c.type === "group") {
      if (mode === "delete") {
        return {
          title: "Supprimer le groupe ?",
          message: "Le groupe et tous ses messages seront supprimés pour tout le monde.",
          confirmLabel: "Supprimer",
        };
      }
      return {
        title: "Quitter le groupe ?",
        message: "Vous ne recevrez plus les messages de ce groupe.",
        confirmLabel: "Quitter",
      };
    }
    return {
      title: "Supprimer la conversation ?",
      message: "La conversation disparaîtra de votre liste.",
      confirmLabel: "Supprimer",
    };
  };

  const requestDeleteConversation = (c: ChatConversation) => {
    setDeleteTarget(c);
  };

  const confirmDeleteConversation = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    const mode = deleteModeFor(deleteTarget);
    const r = await deleteConversation(suiteUserId, deleteTarget.id, mode);
    setDeleteBusy(false);
    if (r.success) {
      const id = deleteTarget.id;
      setDeleteTarget(null);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (selectedId === id) {
        setSelectedId(null);
        setShowInfo(false);
        setMobilePane("list");
      }
    } else {
      setError(r.message || "Action impossible.");
    }
  };

  const handleMarkUnread = async (c: ChatConversation) => {
    const prevCount = c.unread_count ?? 0;
    setConversations((prev) =>
      prev.map((x) => (x.id === c.id ? { ...x, unread_count: Math.max(1, prevCount) } : x))
    );
    const r = await markUnread(suiteUserId, c.id);
    if (r.success && r.data?.conversation) {
      setConversations((prev) =>
        prev.map((x) => (x.id === c.id ? { ...x, ...r.data!.conversation! } : x))
      );
    } else if (r.success && typeof r.data?.unread_count === "number") {
      setConversations((prev) =>
        prev.map((x) => (x.id === c.id ? { ...x, unread_count: r.data!.unread_count } : x))
      );
    } else if (!r.success) {
      setConversations((prev) =>
        prev.map((x) => (x.id === c.id ? { ...x, unread_count: prevCount } : x))
      );
      setError(r.message || "Impossible de passer en non lu.");
    }
  };

  const handleDeleteConversation = () => {
    if (!selected) return;
    requestDeleteConversation(selected);
  };

  const confirmRemoveMember = async () => {
    if (!removeMemberTarget || selectedId == null) return;
    setRemoveMemberBusy(true);
    const r = await removeConversationMember(
      suiteUserId,
      selectedId,
      removeMemberTarget.userId
    );
    setRemoveMemberBusy(false);
    if (r.success && r.data) {
      setConversations((prev) =>
        prev.map((c) => (c.id === r.data!.id ? { ...c, ...r.data } : c))
      );
      setRemoveMemberTarget(null);
    } else {
      setError(r.message || "Impossible de retirer ce participant.");
    }
  };

  const handleAddMember = async (userId: number) => {
    if (selectedId == null) throw new Error("Aucune conversation.");
    const r = await addConversationMember(suiteUserId, selectedId, userId);
    if (r.success && r.data) {
      setConversations((prev) =>
        prev.map((c) => (c.id === r.data!.id ? { ...c, ...r.data } : c))
      );
      return;
    }
    const msg = r.message || "Impossible d’ajouter ce participant.";
    setError(msg);
    throw new Error(msg);
  };

  const handleEditMessage = async () => {
    if (editingMsgId == null) return;
    const body = expandEmoticons(editDraft.trim());
    if (!body) return;
    const r = await editMessage(suiteUserId, editingMsgId, body);
    if (r.success && r.data) {
      setMessages((prev) => prev.map((m) => (m.id === editingMsgId ? { ...m, ...r.data! } : m)));
      setEditingMsgId(null);
      setEditDraft("");
    } else {
      setError(r.message || "Modification impossible.");
    }
  };

  const handleDeleteMessage = async () => {
    if (deleteMessageId == null) return;
    const id = deleteMessageId;
    playChatDeletePop();
    setDeleteMessageBusy(true);
    const r = await deleteMessage(suiteUserId, id);
    setDeleteMessageBusy(false);
    if (r.success) {
      setDeleteMessageId(null);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      void refreshConversations();
    } else {
      setError(r.message || "Suppression impossible.");
    }
  };

  const applyReactions = (messageId: number, reactions: ChatReaction[]) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, reactions } : m))
    );
  };

  const handleReact = async (messageId: number, emoji: string) => {
    const msg = messages.find((m) => m.id === messageId);
    const current = msg ? myReactionOn(msg) : null;
    if (current !== emoji) {
      playChatEmojiPop();
      spawnLocalBurst(emoji, messageId);
    }
    const r = await reactToMessage(suiteUserId, messageId, emoji);
    if (r.success && r.data) {
      applyReactions(messageId, r.data.reactions);
    } else {
      setError(r.message || "Réaction impossible.");
    }
  };

  const myReactionOn = (m: ChatMessage): string | null => {
    const list = m.reactions ?? [];
    for (const r of list) {
      if (r.user_ids.includes(selfId)) return r.emoji;
    }
    return null;
  };

  const myAvatar = chatProfile?.avatar_url ?? null;
  const myLabel = chatProfile?.display_name || profile.username;

  const showHint = (msg: string) => {
    setHint(msg);
    if (hintTimer.current != null) window.clearTimeout(hintTimer.current);
    hintTimer.current = window.setTimeout(() => setHint(null), 1800);
  };

  const handleCopyMessage = (m: ChatMessage) => {
    const text = formatCopyText(m);
    if (!text) {
      setError("Rien à copier.");
      return;
    }
    void copyText(text).then((ok) => {
      if (ok) showHint("Message copié");
      else setError("Impossible de copier.");
    });
  };

  const handleSendAuriz = async () => {
    if (selectedId == null || selected?.type !== "dm" || aurizBusy) return;
    setAurizBusy(true);
    const r = await sendAuriz(suiteUserId, selectedId);
    setAurizBusy(false);
    if (!r.success) {
      setError(r.message || "Auriz impossible.");
      return;
    }
    playChatAuriz();
    setAurizShow({ fromName: myLabel, mine: true, discreet: false });
    showHint("Auriz envoyé");
  };

  const handleForwardTo = async (conversationId: number) => {
    if (!forwardMsg) return;
    setForwardBusy(true);
    const author =
      forwardMsg.sender_id === selfId
        ? myLabel
        : memberLabel(
            colleagues.find((c) => c.id === forwardMsg.sender_id),
            forwardMsg.sender_name || `#${forwardMsg.sender_id}`
          );
    const r = await sendMessage(suiteUserId, {
      conversation_id: conversationId,
      body: formatForwardBody(forwardMsg, author),
    });
    setForwardBusy(false);
    if (r.success && r.data) {
      setForwardMsg(null);
      showHint("Message transféré");
      void refreshConversations();
      if (conversationId === selectedId) {
        setMessages((prev) => (prev.some((m) => m.id === r.data!.id) ? prev : [...prev, r.data!]));
      }
    } else {
      setError(r.message || "Transfert impossible.");
    }
  };

  const joinOngoingCall = async (
    conversationId: number,
    callId: string,
    media: "audio" | "video",
    peerLabel: string
  ) => {
    if (callSession || incomingCall) return;
    const r = await chatCallJoin(suiteUserId, conversationId, callId);
    if (!r.success || !r.data) {
      setError(r.message || "Impossible de rejoindre la visio.");
      void refreshConversations();
      return;
    }
    setIncomingCall(null);
    setCallAnswered(true);
    const session: ChatCallSession = {
      callId: r.data.call_id,
      conversationId,
      roomName: r.data.room,
      livekitUrl: r.data.livekit_url,
      token: r.data.token,
      media: r.data.media ?? media,
      role: "callee",
      peerLabel,
    };
    setCallSession(session);
    setCallChrome(
      session.media === "video" && window.matchMedia("(min-width: 768px)").matches
        ? "dock"
        : "immersive"
    );
    setSuppressJoinCallId(null);
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              active_call: {
                call_id: session.callId,
                room: session.roomName,
                media: session.media,
                status: "active",
                joinable: true,
              },
            }
          : c
      )
    );
  };

  const startCallForConversation = async (
    conversationId: number,
    media: "audio" | "video",
    peerLabel: string,
    inviteeIds?: number[]
  ): Promise<string | null> => {
    if (callSession) return callSession.callId;

    playChatCallCalling();
    const r = await chatCallInvite(suiteUserId, conversationId, media, inviteeIds);
    if (!r.success || !r.data) {
      stopChatCallCalling();
      if (r.error === "peer_in_call") {
        setError(r.message || "Cette personne est déjà en visio.");
        return null;
      }
      // Vraie visio encore active → Rejoindre ; fantôme → invite a dû le clôturer, on retente
      if (r.error === "call_already_active") {
        const again = await chatCallActive(suiteUserId, conversationId);
        const call = again.success ? again.data?.call : null;
        if (call) {
          if (call.joinable === false || !chatCallIsJoinable(call)) {
            setError("Cette visio est réservée aux participants choisis.");
            return null;
          }
          await joinOngoingCall(
            conversationId,
            call.call_id,
            call.media === "audio" ? "audio" : media,
            peerLabel
          );
          return call.call_id;
        }
        const retry = await chatCallInvite(suiteUserId, conversationId, media, inviteeIds);
        if (retry.success && retry.data) {
          const convMeta = conversations.find((c) => c.id === conversationId);
          if (isOpenVisioConversation(convMeta)) setCallAnswered(true);
          else setCallAnswered(false);
          setIncomingCall(null);
          const session: ChatCallSession = {
            callId: retry.data.call_id,
            conversationId,
            roomName: retry.data.room,
            livekitUrl: retry.data.livekit_url,
            token: retry.data.token,
            media: retry.data.media ?? media,
            role: "caller",
            peerLabel,
          };
          setCallSession(session);
          setCallChrome(
            session.media === "video" && window.matchMedia("(min-width: 768px)").matches
              ? "dock"
              : "immersive"
          );
          setConversations((prev) =>
            prev.map((c) =>
              c.id === conversationId
                ? {
                    ...c,
                    active_call: {
                      call_id: session.callId,
                      room: session.roomName,
                      media: session.media,
                      status: isOpenVisioConversation(convMeta) ? "active" : "ringing",
                      joinable: true,
                    },
                  }
                : c
            )
          );
          clearRingTimer();
          return session.callId;
        }
      }
      setError(r.message || "Impossible de démarrer l’appel.");
      void refreshConversations();
      return null;
    }
    // Groupe ouvert sans sonnerie : stop calling tone
    const convMeta = conversations.find((c) => c.id === conversationId);
    if (isOpenVisioConversation(convMeta)) {
      stopChatCallCalling();
      setCallAnswered(true);
    }
    setIncomingCall(null);
    if (!isOpenVisioConversation(convMeta)) {
      setCallAnswered(false);
    }
    const session: ChatCallSession = {
      callId: r.data.call_id,
      conversationId,
      roomName: r.data.room,
      livekitUrl: r.data.livekit_url,
      token: r.data.token,
      media: r.data.media ?? media,
      role: "caller",
      peerLabel,
    };
    setCallSession(session);
    setCallChrome(
      session.media === "video" && window.matchMedia("(min-width: 768px)").matches
        ? "dock"
        : "immersive"
    );
    setConversations((prev) =>
      prev.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              active_call: {
                call_id: session.callId,
                room: session.roomName,
                media: session.media,
                status: isOpenVisioConversation(convMeta) ? "active" : "ringing",
                joinable: true,
              },
            }
          : c
      )
    );
    clearRingTimer();
    // DM : timer « pas de réponse ». Salle / groupe : on attend l’invité, pas de raccrochage auto.
    const allowMissTimer = !isOpenVisioConversation(convMeta) && convMeta?.type === "dm";
    if (allowMissTimer) {
      const timeoutSec =
        typeof r.data.ring_timeout_sec === "number" ? r.data.ring_timeout_sec : 40;
      ringTimerRef.current = window.setTimeout(() => {
        void (async () => {
          const cur = callSessionRef.current;
          if (!cur || cur.callId !== session.callId || cur.role !== "caller") return;
          setCallSession(null);
          await chatCallMiss(suiteUserId, cur.conversationId, cur.callId, cur.media);
          setCallAnswered(false);
          playChatCallClose();
          setError("Pas de réponse");
          void refreshConversations();
        })();
      }, Math.max(15, timeoutSec) * 1000);
    }
    return session.callId;
  };

  const startCall = async (media: "audio" | "video", opts?: { skipPick?: boolean }) => {
    if (!selected || callSession) return;
    if (selected.type !== "dm" && selected.type !== "group") return;
    if (selected.type === "dm") {
      const peerId = selected.member_ids.map((id) => Number(id)).find((id) => id !== selfId);
      const thisLive =
        !!selected.active_call && selected.active_call.call_id !== suppressJoinCallId;
      const canJoinHere = thisLive && chatCallIsJoinable(selected.active_call);
      const extra = colleagues.find((c) => Number(c.id) === peerId)?.presence;
      if (peerId != null && isChatPeerBusyInCall(peerId, presence, extra) && !canJoinHere) {
        setError("Cette personne est déjà en visio — l’appel n’est pas disponible.");
        return;
      }
    }
    if (selected.active_call && selected.active_call.call_id !== suppressJoinCallId) {
      if (selected.active_call.joinable === false || !chatCallIsJoinable(selected.active_call)) {
        setError("Cette visio est réservée aux participants choisis.");
        return;
      }
      await joinOngoingCall(
        selected.id,
        selected.active_call.call_id,
        selected.active_call.media === "audio" ? "audio" : media,
        conversationTitle(selected, selfId)
      );
      return;
    }
    const others = selected.member_ids.filter((id) => id !== selfId);
    // Groupe / salle : tout le monde est coché par défaut → sonnerie + push (comme avant)
    if (!opts?.skipPick && selected.type === "group" && others.length >= 1) {
      setCallPickMedia(media);
      setCallPickMembers(others.slice(0, 3));
      setShowCallPick(true);
      return;
    }
    const inviteeIds = opts?.skipPick
      ? []
      : selected.type === "group" && selfId != null
        ? others
        : undefined;
    await startCallForConversation(
      selected.id,
      media,
      conversationTitle(selected, selfId),
      inviteeIds
    );
  };

  const confirmCallPick = async (openSilent = false) => {
    if (!selected) return;
    const invitees = openSilent ? [] : callPickMembers;
    if (!openSilent && invitees.length === 0) {
      setError("Cochez au moins une personne, ou ouvrez sans appeler.");
      return;
    }
    if (invitees.length > 3) {
      setError("Maximum 3 invités (4 personnes au total).");
      return;
    }
    setShowCallPick(false);
    await startCallForConversation(
      selected.id,
      callPickMedia,
      conversationTitle(selected, selfId),
      invitees
    );
    setCallPickMembers([]);
  };

  const startVideoWithColleague = async (userId: number) => {
    if (callSession || incomingCall) return;
    const colleague = colleagues.find((c) => c.id === userId);
    if (isChatPeerBusyInCall(userId, presence, colleague?.presence)) {
      setError("Cette personne est déjà en visio — l’appel n’est pas disponible.");
      return;
    }
    const peerLabel =
      colleague?.display_name?.trim() || colleague?.username || `Contact #${userId}`;
    const existing = conversations.find(
      (c) => c.type === "dm" && c.member_ids.includes(userId) && c.member_ids.includes(selfId)
    );
    if (existing) {
      selectConversation(existing.id);
      setShowNewVideo(false);
      await startCallForConversation(existing.id, "video", peerLabel);
      return;
    }
    const r = await createConversation(suiteUserId, { type: "dm", member_ids: [userId] });
    if (r.success && r.data) {
      await refreshConversations();
      selectConversation(r.data.id);
      setShowNewVideo(false);
      await startCallForConversation(r.data.id, "video", peerLabel);
    } else {
      setError(
        r.error === "no_common_client"
          ? "Pas de client Chat en commun avec cette personne."
          : r.message || "Impossible de démarrer la visio."
      );
    }
  };

  const acceptIncomingCall = async () => {
    if (!incomingCall || callSession) return;
    const inv = incomingCall;
    clearRingTimer();
    // Décrochage : Open immédiat (coupe ringtone) dans le geste clic
    playChatCallOpen();
    const r = await chatCallAccept(suiteUserId, inv.conversationId, inv.callId);
    if (!r.success || !r.data) {
      playChatCallClose();
      setError(r.message || "Impossible d’accepter l’appel.");
      setIncomingCall(null);
      return;
    }
    setIncomingCall(null);
    setCallAnswered(true);
    setCallSession({
      callId: inv.callId,
      conversationId: inv.conversationId,
      roomName: r.data.room || inv.room,
      livekitUrl: r.data.livekit_url,
      token: r.data.token,
      media: inv.media,
      role: "callee",
      peerLabel: inv.fromName,
    });
    setCallChrome(
      inv.media === "video" && window.matchMedia("(min-width: 768px)").matches
        ? "dock"
        : "immersive"
    );
  };

  const rejectIncomingCall = async () => {
    if (!incomingCall) return;
    const inv = incomingCall;
    setIncomingCall(null);
    clearRingTimer();
    // Refus = raccrochage sonore immédiat
    playChatCallClose();
    await chatCallReject(suiteUserId, inv.conversationId, inv.callId);
  };

  const hangupCall = async () => {
    const cur = callSession;
    clearRingTimer();
    playChatCallClose();
    if (cur) {
      // Pas de flash « Rejoindre » sur cet appareil pendant le raccrochage
      setSuppressJoinCallId(cur.callId);
      setConversations((prev) =>
        prev.map((c) =>
          c.id === cur.conversationId && c.active_call?.call_id === cur.callId
            ? { ...c, active_call: null }
            : c
        )
      );
    }
    setCallSession(null);
    setCallAnswered(false);
    setCallChrome("immersive");
    if (cur) {
      await chatCallHangup(suiteUserId, cur.conversationId, cur.callId);
      await refreshConversations();
      // Après refresh : si la salle est encore joignable, réafficher Rejoindre
      window.setTimeout(() => {
        setSuppressJoinCallId((id) => (id === cur.callId ? null : id));
      }, 800);
    }
  };

  useEffect(() => {
    if (!selectedId || callSession) return;
    let cancelled = false;
    const syncActive = () => {
      void chatCallActive(suiteUserId, selectedId).then((r) => {
        if (cancelled || !r.success) return;
        const call = r.data?.call ?? null;
        setConversations((prev) =>
          prev.map((c) => {
            if (c.id !== selectedId) return c;
            if (!call) return { ...c, active_call: null };
            return {
              ...c,
              active_call: {
                call_id: call.call_id,
                room: call.room,
                media: call.media,
                status: call.status,
                created_by: call.created_by,
                joinable: chatCallIsJoinable(call),
              },
            };
          })
        );
      });
    };
    syncActive();
    const timer = window.setInterval(syncActive, 10000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [selectedId, callSession, suiteUserId]);

  useEffect(() => {
    if (!callSession) return;
    const desktop =
      typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
    setCallChrome(callSession.media === "video" && desktop ? "dock" : "immersive");
  }, [callSession?.callId, callSession?.media]);

  // Keepalive anti-fantômes pendant la visio
  useEffect(() => {
    if (!callSession) return;
    const tick = () => {
      void chatCallTouch(suiteUserId, callSession.conversationId, callSession.callId);
    };
    tick();
    const id = window.setInterval(tick, 20_000);
    return () => window.clearInterval(id);
  }, [callSession?.callId, callSession?.conversationId, suiteUserId]);

  return (
    <div
      className={`chat-app relative flex h-dvh min-h-0 overflow-hidden ${
        aurizShow && !aurizShow.discreet ? "chat-auriz--shake" : ""
      }`}
    >
      {aurizShow ? (
        <ChatAurizOverlay
          fromName={aurizShow.fromName}
          mine={aurizShow.mine}
          discreet={aurizShow.discreet}
          onDone={() => setAurizShow(null)}
        />
      ) : null}
      {incomingCall && !callSession ? (
        <ChatIncomingCallBanner
          peerLabel={incomingCall.fromName}
          media={incomingCall.media}
          onAccept={() => void acceptIncomingCall()}
          onReject={() => void rejectIncomingCall()}
        />
      ) : null}
      {callSession ? (
        <ChatCallOverlay
          session={callSession}
          chrome={callChrome}
          onChromeChange={setCallChrome}
          awaitingGuest={isOpenVisioConversation(
            conversations.find((c) => c.id === callSession.conversationId)
          )}
          onHangup={() => void hangupCall()}
          onRemoteJoined={() => {
            clearRingTimer();
            setCallAnswered(true);
            playChatCallOpen();
          }}
          onLocalConnected={() => {
            // Salle ouverte localement : ne plus auto-terminer « pas de réponse »
            clearRingTimer();
            const conv = conversations.find((c) => c.id === callSession.conversationId);
            if (isOpenVisioConversation(conv)) setCallAnswered(true);
            setConversations((prev) =>
              prev.map((c) =>
                c.id === callSession.conversationId && c.active_call
                  ? { ...c, active_call: { ...c.active_call, status: "active" } }
                  : c
              )
            );
            void chatCallTouch(suiteUserId, callSession.conversationId, callSession.callId);
          }}
          onSuperseded={() => {
            clearRingTimer();
            playChatCallClose();
            setCallSession(null);
            setCallAnswered(false);
            setCallChrome("immersive");
            setError("Appel repris sur un autre appareil.");
            void refreshConversations();
          }}
          canInviteOthers={
            conversations.find((c) => c.id === callSession.conversationId)?.type === "group" ||
            conversations.find((c) => c.id === callSession.conversationId)?.is_room === true
          }
          inviteCandidates={(() => {
            const conv = conversations.find((c) => c.id === callSession.conversationId);
            const memberSet = new Set(conv?.member_ids ?? []);
            const cid = conv?.client_id;
            return colleagues
              .filter((c) => {
                if (c.id === selfId) return false;
                if (!cid) return true;
                const ids = c.client_ids ?? c.clients?.map((cl) => cl.id) ?? [];
                return ids.length === 0 || ids.includes(cid);
              })
              .map((c) => ({
                id: c.id,
                label: c.display_name?.trim() || c.username || `#${c.id}`,
                presence: normalizeChatPresence(presence[c.id] ?? c.presence),
                avatarUrl: c.avatar_url ?? null,
                clients: (c.clients ?? []).map((cl) => cl.name).filter(Boolean),
                inGroup: memberSet.has(c.id),
              }))
              .sort((a, b) => Number(b.inGroup) - Number(a.inGroup) || a.label.localeCompare(b.label, "fr"));
          })()}
          onInviteParticipant={async (userId) => {
            const r = await chatCallAddParticipant(
              suiteUserId,
              callSession.conversationId,
              callSession.callId,
              userId
            );
            if (!r.success) {
              setError(r.message || "Impossible d’inviter.");
              return;
            }
            await refreshConversations();
          }}
          onInviteGuest={async (email) => {
            if (!email || !email.includes("@")) {
              setError("Indiquez un e-mail pour l’invité.");
              return null;
            }
            const g = await createChatGuestInvite(suiteUserId, {
              conversationId: callSession.conversationId,
              callId: callSession.callId,
              email,
              startsAt: meetings.find((m) => m.status === "scheduled")?.starts_at,
            });
            if (!g.success || !g.data) {
              setError(g.message || "Impossible d’inviter.");
              return null;
            }
            await copyText(g.data.join_url);
            return g.data.join_url;
          }}
          onCopyGuestLink={async () => {
            const g = await createChatGuestInvite(suiteUserId, {
              conversationId: callSession.conversationId,
              callId: callSession.callId,
              startsAt: meetings.find((m) => m.status === "scheduled")?.starts_at,
            });
            if (!g.success || !g.data) {
              setError(g.message || "Impossible de créer le lien.");
              return null;
            }
            await copyText(g.data.join_url);
            return g.data.join_url;
          }}
        />
      ) : null}
      <ChatConversationList
        selfId={selfId}
        mobilePane={mobilePane}
        myLabel={myLabel}
        myAvatar={myAvatar}
        myStatus={myStatus}
        search={search}
        onSearchChange={setSearch}
        loadingList={loadingList}
        filtered={filtered}
        selectedId={selectedId}
        presence={presence}
        colleagues={viewColleagues}
        suppressJoinCallId={suppressJoinCallId}
        myCallId={callSession?.callId ?? null}
        showCreateMenu={showCreateMenu}
        showTheme={showTheme}
        onOpenProfile={() => {
          setShowTheme(false);
          setShowPicker(false);
          setShowProfile(true);
        }}
        onToggleCreateMenu={() => {
          setShowTheme(false);
          setShowCreateMenu((v) => !v);
        }}
        onCloseCreateMenu={() => setShowCreateMenu(false)}
        onOpenNewDm={() => {
          setShowCreateMenu(false);
          setShowNewDm(true);
        }}
        onOpenNewGroup={() => {
          setShowCreateMenu(false);
          setGroupTitle("");
          setGroupMembers([]);
          setGroupAsVisioRoom(false);
          setShowNewGroup(true);
        }}
        onOpenNewGuest={() => {
          setShowCreateMenu(false);
          setShowNewGuest(true);
        }}
        onToggleTheme={() => {
          setShowPicker(false);
          setShowCreateMenu(false);
          setShowTheme((v) => !v);
        }}
        onCloseTheme={() => setShowTheme(false)}
        onSelectConversation={selectConversation}
        onMarkUnread={(c) => void handleMarkUnread(c)}
        onRequestDelete={requestDeleteConversation}
        onOpenAvatarPreview={openAvatarPreview}
        pinnedIds={sidebarOrder.pinned}
        onTogglePin={handleTogglePin}
      />

      <ChatThread
        suiteUserId={suiteUserId}
        selfId={selfId}
        selfAvatarUrl={myAvatar}
        selfLabel={myLabel}
        mobilePane={mobilePane}
        selected={selected}
        colleagues={viewColleagues}
        presence={presence}
        lastSeen={lastSeen}
        messages={messages}
        loadingThread={loadingThread}
        typingLabel={typingLabel}
        wallpaperPrefs={wallpaperPrefs}
        threadFileDragOver={threadFileDragOver}
        callSession={callSession}
        incomingCall={incomingCall}
        suppressJoinCallId={suppressJoinCallId}
        draft={draft}
        files={files}
        showPicker={showPicker}
        sendEffectsOpen={sendEffectsOpen}
        replyTo={replyTo}
        editingMsgId={editingMsgId}
        editDraft={editDraft}
        effectReplay={effectReplay}
        flashReplyId={flashReplyId}
        threadPaneRef={threadPaneRef}
        threadScrollRef={threadScrollRef}
        threadEndRef={threadEndRef}
        draftRef={draftRef}
        onLeaveToSalon={leaveToSalon}
        onOpenAvatarPreview={openAvatarPreview}
        onJoinOngoingCall={(cid, callId, media, peerLabel) =>
          void joinOngoingCall(cid, callId, media, peerLabel)
        }
        onStartCall={(media) => void startCall(media)}
        onToggleInfo={() => setShowInfo((v) => !v)}
        onThreadDragEnter={onThreadDragEnter}
        onThreadDragOver={onThreadDragOver}
        onThreadDragLeave={onThreadDragLeave}
        onThreadDrop={onThreadDrop}
        onOpenMsgMenu={openMsgMenu}
        onBubblePointerDown={onBubblePointerDown}
        onBubblePointerMove={onBubblePointerMove}
        onClearLongPress={clearLongPress}
        onOpenMediaLightbox={openMediaLightbox}
        onEditDraftChange={setEditDraft}
        onCancelEdit={() => {
          setEditingMsgId(null);
          setEditDraft("");
        }}
        onSaveEdit={() => void handleEditMessage()}
        onReact={(id, emoji) => void handleReact(id, emoji)}
        onBeginReply={beginReply}
        onCopyMessage={handleCopyMessage}
        onForwardMessage={(m) => {
          setCtxMenu(null);
          setForwardMsg(m);
        }}
        onScrollToMessage={scrollToMessage}
        onReplayEffect={(id) =>
          setEffectReplay((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }))
        }
        resolveReplyPreview={resolveReplyPreview}
        replyAuthorLabel={replyAuthorLabel}
        myReactionOn={myReactionOn}
        onRemoveFile={(i) => setFiles((prev) => prev.filter((_, j) => j !== i))}
        onAddComposerFiles={addComposerFiles}
        onEmoji={insertEmoji}
        onSendGif={(g) => void handleSendGif(g)}
        onClosePicker={() => setShowPicker(false)}
        onTogglePicker={() => {
          setShowTheme(false);
          setShowPicker((v) => !v);
        }}
        onCancelReply={() => setReplyTo(null)}
        onDraftChange={onDraftChange}
        onSend={(effect) => void handleSend(effect)}
        onSendVoice={(file) => void handleSendVoice(file)}
        onSendCapturedPhoto={(file, caption) => handleSendCapturedPhoto(file, caption)}
        onCreatePoll={(q, opts) => void handleCreatePoll(q, opts)}
        onVotePoll={(id, opt) => void handleVotePoll(id, opt)}
        onClosePoll={(id) => void handleClosePoll(id)}
        onVoiceError={(msg) => setError(msg)}
        onOpenSendEffects={openSendEffectsMenu}
        onCloseSendEffects={() => setSendEffectsOpen(false)}
        onOpenNewDm={() => setShowNewDm(true)}
        onOpenNewGroup={() => setShowNewGroup(true)}
        onOpenNewVideo={() => setShowNewVideo(true)}
        aurizReady={aurizElig.ready && !aurizBusy}
        aurizTitle={aurizElig.title}
        onAuriz={() => void handleSendAuriz()}
        meetings={meetings}
        onJoinMeeting={() => void startCall("video", { skipPick: true })}
      />

      {callSession?.media === "video" && callChrome === "dock" ? (
        <div className="chat-call-dock-spacer hidden md:block" aria-hidden />
      ) : null}

      {selected ? (
        <ChatInfoPanel
          open={showInfo}
          selected={selected}
          selfId={selfId}
          myLabel={myLabel}
          myStatus={myStatus}
          colleagues={viewColleagues}
          presence={presence}
          renameDraft={renameDraft}
          onRenameDraftChange={setRenameDraft}
          wallpaperPrefs={wallpaperPrefs}
          onWallpaperChange={handleWallpaperChange}
          onClose={() => {
            if (!dismissOverlay()) setShowInfo(false);
          }}
          onRename={() => void handleRenameConversation()}
          onGroupAvatar={(f) => void handleGroupAvatar(f)}
          onDelete={() => void handleDeleteConversation()}
          onRemoveMember={(userId, name) => setRemoveMemberTarget({ userId, name })}
          onAddMember={handleAddMember}
          onOpenAvatarPreview={openAvatarPreview}
          suiteUserId={suiteUserId}
          meetings={meetings}
          onRefreshMeetings={() => {
            if (selectedId != null) {
              void listChatMeetings(suiteUserId, selectedId).then((r) => {
                if (r.success && r.data) setMeetings(r.data);
              });
            }
          }}
          onJoinMeeting={() => void startCall("video", { skipPick: true })}
          nicknames={nicknames}
          officialName={officialPeerLabel}
          onSaveNickname={handleSaveNickname}
        />
      ) : null}

      {ctxMenu ? (
        <MessageContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          canEdit={ctxMenu.message.sender_id === selfId && !isChatPollMessage(ctxMenu.message)}
          canDelete={ctxMenu.message.sender_id === selfId}
          myReaction={myReactionOn(ctxMenu.message)}
          sheet={ctxMenu.sheet}
          openedAt={longPressOpenedAt.current}
          onClose={() => setCtxMenu(null)}
          onReact={(emoji) => {
            const id = ctxMenu.message.id;
            armMenuAction();
            setCtxMenu(null);
            void handleReact(id, emoji);
          }}
          onReply={() => beginReply(ctxMenu.message)}
          onCopy={() => {
            const m = ctxMenu.message;
            armMenuAction();
            setCtxMenu(null);
            handleCopyMessage(m);
          }}
          onForward={() => {
            const m = ctxMenu.message;
            armMenuAction();
            setCtxMenu(null);
            setForwardMsg(m);
          }}
          onEdit={() => {
            armMenuAction();
            setEditingMsgId(ctxMenu.message.id);
            setEditDraft(ctxMenu.message.body);
            setCtxMenu(null);
          }}
          onDelete={() => {
            const id = ctxMenu.message.id;
            armMenuAction();
            setCtxMenu(null);
            window.setTimeout(() => setDeleteMessageId(id), 60);
          }}
          hasFiles={collectMessageSaveTargets(ctxMenu.message).length > 0}
          hasImages={collectMessageSaveTargets(ctxMenu.message).some(isImageAttachment)}
          onDownload={() => {
            const atts = collectMessageSaveTargets(ctxMenu.message);
            armMenuAction();
            setCtxMenu(null);
            void runDownloadAtts(atts);
          }}
          onSaveCoffre={() => {
            const atts = collectMessageSaveTargets(ctxMenu.message);
            armMenuAction();
            setCtxMenu(null);
            setSavePicker({ kind: "coffre", atts });
          }}
          onSavePhotos={() => {
            const atts = collectMessageSaveTargets(ctxMenu.message).filter(isImageAttachment);
            armMenuAction();
            setCtxMenu(null);
            if (atts.length) setSavePicker({ kind: "photos", atts });
          }}
        />
      ) : null}

      {lightbox ? (
        <ImageLightbox
          src={lightbox.src}
          alt={lightbox.alt}
          variant={lightbox.variant}
          saveBusy={saveBusy}
          onClose={() => {
            if (!dismissOverlay()) setLightbox(null);
          }}
          onDownload={
            lightbox.variant === "avatar" || !lightbox.attachment
              ? undefined
              : () => void runDownloadAtts([lightbox.attachment!])
          }
          onSaveCoffre={
            lightbox.variant === "avatar" || !lightbox.attachment
              ? undefined
              : () => {
                  const att = lightbox.attachment!;
                  setLightbox(null);
                  setSavePicker({ kind: "coffre", atts: [att] });
                }
          }
          onSavePhotos={
            lightbox.variant === "avatar" ||
            !lightbox.attachment ||
            !isImageAttachment(lightbox.attachment)
              ? undefined
              : () => {
                  const att = lightbox.attachment!;
                  setLightbox(null);
                  setSavePicker({ kind: "photos", atts: [att] });
                }
          }
        />
      ) : null}

      <ChatDestFolderPicker
        open={savePicker != null}
        kind={savePicker?.kind ?? "coffre"}
        filename={
          savePicker && savePicker.atts.length === 1
            ? savePicker.atts[0].name
            : savePicker
              ? `${savePicker.atts.length} fichiers`
              : undefined
        }
        onClose={() => {
          if (!saveBusy) setSavePicker(null);
        }}
        onConfirm={async (folderPath) => {
          if (!savePicker) return;
          setSaveBusy(true);
          const r =
            savePicker.kind === "coffre"
              ? await saveChatAttachmentsToCoffre(savePicker.atts, folderPath)
              : await saveChatAttachmentsToPhotos(savePicker.atts, folderPath);
          setSaveBusy(false);
          if (r.ok) {
            setSavePicker(null);
            reportSave(
              true,
              savePicker.kind === "coffre" ? "Enregistré dans le Coffre." : "Enregistré dans Photos."
            );
          } else {
            reportSave(false, "", r.error);
          }
        }}
      />

      <ChatConfirmDialog
        open={deleteTarget != null}
        title={deleteTarget ? deleteCopyFor(deleteTarget).title : ""}
        message={deleteTarget ? deleteCopyFor(deleteTarget).message : undefined}
        confirmLabel={deleteTarget ? deleteCopyFor(deleteTarget).confirmLabel : "Supprimer"}
        busy={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setDeleteTarget(null);
        }}
        onConfirm={() => void confirmDeleteConversation()}
      />

      <ChatConfirmDialog
        open={removeMemberTarget != null}
        title={
          removeMemberTarget
            ? `Retirer ${removeMemberTarget.name} ?`
            : "Retirer ce participant ?"
        }
        message="Cette personne n’aura plus accès au groupe ni à l’historique."
        confirmLabel="Retirer"
        busy={removeMemberBusy}
        onCancel={() => {
          if (!removeMemberBusy) setRemoveMemberTarget(null);
        }}
        onConfirm={() => void confirmRemoveMember()}
      />

      <ChatConfirmDialog
        open={deleteMessageId != null}
        title="Supprimer ce message ?"
        message="Le message sera retiré de la conversation pour tout le monde."
        confirmLabel="Supprimer"
        busy={deleteMessageBusy}
        onCancel={() => {
          if (!deleteMessageBusy) setDeleteMessageId(null);
        }}
        onConfirm={() => void handleDeleteMessage()}
      />

      {showProfile ? (
        <ChatProfileModal
          suiteUserId={suiteUserId}
          username={profile.username}
          status={myStatus}
          onStatusChange={setMyStatus}
          onClose={() => setShowProfile(false)}
          onUpdated={(p) => setChatProfile(p)}
        />
      ) : null}

      {showNewDm ? (
        <ChatNewDmModal
          selfId={selfId}
          colleagues={viewColleagues}
          presence={presence}
          onClose={() => setShowNewDm(false)}
          onStartDm={(id) => void startDm(id)}
          onOpenAvatarPreview={openAvatarPreview}
        />
      ) : null}

      {showNewVideo ? (
        <ChatNewVideoModal
          selfId={selfId}
          colleagues={viewColleagues}
          presence={presence}
          onClose={() => setShowNewVideo(false)}
          onStartVideo={(id) => void startVideoWithColleague(id)}
          onOpenAvatarPreview={openAvatarPreview}
        />
      ) : null}

      {showCallPick && selected ? (
        <ChatCallPickModal
          selected={selected}
          selfId={selfId}
          colleagues={viewColleagues}
          presence={presence}
          callPickMembers={callPickMembers}
          onToggleMember={(id, checked) => {
            setCallPickMembers((prev) => (checked ? prev.filter((x) => x !== id) : [...prev, id]));
          }}
          onClose={() => setShowCallPick(false)}
          onConfirm={() => void confirmCallPick(false)}
          onOpenSilent={() => void confirmCallPick(true)}
        />
      ) : null}

      {showNewGroup ? (
        <ChatNewGroupModal
          selfId={selfId}
          colleagues={viewColleagues}
          presence={presence}
          myClientIds={myClientIds}
          groupTitle={groupTitle}
          groupMembers={groupMembers}
          groupAsVisioRoom={groupAsVisioRoom}
          onTitleChange={setGroupTitle}
          onToggleVisioRoom={(on) => {
            setGroupAsVisioRoom(on);
            if (on) setGroupMembers((prev) => prev.slice(0, 3));
          }}
          onToggleMember={(id, checked) => {
            setGroupMembers((prev) => (checked ? prev.filter((x) => x !== id) : [...prev, id]));
          }}
          onClose={() => setShowNewGroup(false)}
          onCreate={() => void startGroup()}
          onOpenAvatarPreview={openAvatarPreview}
        />
      ) : null}

      {showNewGuest ? (
        <ChatGuestInviteModal
          onClose={() => setShowNewGuest(false)}
          onSubmit={startGuestFromPlus}
        />
      ) : null}

      <ChatForwardPicker
        open={forwardMsg != null}
        conversations={conversations}
        currentId={selectedId}
        selfId={selfId}
        busy={forwardBusy}
        onPick={(id) => void handleForwardTo(id)}
        onClose={() => {
          if (!forwardBusy) setForwardMsg(null);
        }}
      />

      {hint ? (
        <div className="fixed bottom-4 left-1/2 z-50 max-w-sm -translate-x-1/2 rounded-xl border border-[var(--chat-border)] bg-[var(--chat-surface)] px-4 py-2 text-sm text-[var(--chat-text)] shadow-[var(--chat-shadow)]">
          {hint}
        </div>
      ) : null}

      {error ? (
        <div className="fixed bottom-4 left-1/2 z-50 max-w-sm -translate-x-1/2 rounded-xl border border-[var(--chat-border)] bg-[var(--chat-surface)] px-4 py-2 text-sm shadow-[var(--chat-shadow)]">
          <div className="flex items-start gap-2">
            <p
              className={`flex-1 ${
                /déjà en visio|appel n’est pas disponible|Enregistré|téléchargé/i.test(error)
                  ? "font-medium text-[#0f766e]"
                  : "text-[var(--chat-danger)]"
              }`}
            >
              {error}
            </p>
            <button type="button" onClick={() => setError(null)}>
              <X className="size-4" />
            </button>
          </div>
        </div>
      ) : null}

      <ChatEmojiBurstLayer shower={shower} local={local} />
      <ChatPwaOnboard suiteUserId={suiteUserId} />
    </div>
  );
}

export default function App() {
  const guestToken = readGuestToken();
  if (guestToken) {
    return (
      <ChatThemeProvider forceTheme="dark">
        <ChatGuestJoin token={guestToken} />
      </ChatThemeProvider>
    );
  }
  return <ChatApp />;
}

function ChatApp() {
  const [suiteUserId, setSuiteUserId] = useState<string | null>(() => {
    if (isChatStandalone() || isChatHub2Desk()) return null;
    syncSuiteUserIdFromQuery();
    return readStoredSuiteUserId();
  });
  const [profile, setProfile] = useState<SuiteUserProfile | null>(null);
  const [gate, setGate] = useState<GateState>("boot");
  const [bridge, setBridge] = useState<BridgeState>({ status: "idle" });

  const gateRef = useRef(gate);
  const profileRef = useRef(profile);
  /** Hub 2 : whoami+ping déjà faits — évite un 2ᵉ « Vérification de la session… ». */
  const cookieBootDoneRef = useRef(false);
  gateRef.current = gate;
  profileRef.current = profile;

  const showForbidden = useCallback(() => {
    window.hideSuiteAuthModal?.();
    setGate("forbidden");
    window.showSuiteForbiddenModal?.({
      actions: [
        {
          label: "Actualiser",
          primary: true,
          onClick: () => {
            window.hideSuiteForbiddenModal?.();
            window.location.reload();
          },
        },
      ],
    });
  }, []);

  const handleSuiteSessionLost = useCallback((_message?: string | null) => {
    window.__chatSessionVerified = false;
    cookieBootDoneRef.current = false;
    window.stopSuiteIframeSsoPoll?.();
    window.stopSuiteHubSessionWatch?.();
    clearStoredSuiteUserIds();
    setSuiteUserId(null);
    setProfile(null);
    setGate("need_login");
    setBridge({ status: "idle" });
    window.hideSuiteForbiddenModal?.();
    window.showSuiteAuthModal?.(undefined, { autoReconnect: false });
  }, []);

  const onSuiteAuthenticated = useCallback(async (userId: number) => {
    resetChatSessionGateEmit();
    window.hideSuiteForbiddenModal?.();
    // Bureau hub 2 : reprendre whoami+ping (ne pas rester bloqué sur « checking »).
    if (isChatHub2Desk()) {
      cookieBootDoneRef.current = false;
      setGate("checking");
      const r = await fetchSsoVerify("");
      if (r.ok && r.profile) {
        cookieBootDoneRef.current = true;
        window.__chatSessionVerified = true;
        setSuiteUserId(String(r.profile.id));
        setProfile(r.profile);
        setGate("ready");
        setBridge({ status: "ok", serverUserId: r.profile.id });
        window.hideSuiteAuthModal?.();
        return;
      }
      window.__chatSessionVerified = false;
      setProfile(null);
      if (r.forbidden) {
        showForbidden();
        return;
      }
      setGate("need_login");
      window.showSuiteAuthModal?.(undefined, { autoReconnect: false });
      return;
    }
    if (typeof window.persistSuiteUserId === "function") {
      window.persistSuiteUserId(userId);
    }
    setSuiteUserId(String(userId));
    setGate("checking");
  }, [showForbidden]);

  useEffect(() => {
    window.__chatIsConnected = () =>
      window.__chatSessionVerified === true &&
      profileRef.current !== null &&
      gateRef.current === "ready";

    window.__chatOnAuthenticated = onSuiteAuthenticated;
    window.__chatOnSessionLost = handleSuiteSessionLost;

    return () => {
      delete window.__chatIsConnected;
      delete window.__chatOnAuthenticated;
      delete window.__chatOnSessionLost;
    };
  }, [handleSuiteSessionLost, onSuiteAuthenticated]);

  useEffect(() => {
    const onSuiteUser = (e: Event) => {
      if (isChatHub2Desk() || cookieBootDoneRef.current) return;
      const ce = e as CustomEvent<{ userId: string | number }>;
      if (ce.detail?.userId != null) {
        setSuiteUserId(String(ce.detail.userId));
      }
    };
    window.addEventListener("suiteUserIdReceived", onSuiteUser);
    const onMissing = () => {
      if (isChatHub2Desk() || cookieBootDoneRef.current) return;
      setSuiteUserId(null);
      setGate("need_login");
    };
    window.addEventListener("aurelia-chat-desk-missing", onMissing);
    const onHubSessionLost = () => {
      handleSuiteSessionLost();
    };
    window.addEventListener("suiteHubSessionLost", onHubSessionLost);
    return () => {
      window.removeEventListener("suiteUserIdReceived", onSuiteUser);
      window.removeEventListener("aurelia-chat-desk-missing", onMissing);
      window.removeEventListener("suiteHubSessionLost", onHubSessionLost);
    };
  }, [handleSuiteSessionLost]);

  const chatReady = gate === "ready" && bridge.status === "ok" && profile;

  useEffect(() => {
    if (chatReady) {
      window.hideSuiteAuthModal?.();
    } else if (gate === "need_login" || gate === "error") {
      window.showSuiteAuthModal?.(undefined, { autoReconnect: false });
    }
  }, [gate, chatReady]);

  /* Bureau hub 2 : une seule vérif cookie (whoami + ping, retries). Pas de 2ᵉ ping bridge. */
  useEffect(() => {
    if (!isChatHub2Desk()) return undefined;
    if (cookieBootDoneRef.current && window.__chatSessionVerified) return undefined;
    let cancelled = false;
    setGate("checking");
    void fetchSsoVerify("").then((r) => {
      if (cancelled) return;
      if (r.ok && r.profile) {
        cookieBootDoneRef.current = true;
        window.__chatSessionVerified = true;
        resetChatSessionGateEmit();
        setSuiteUserId(String(r.profile.id));
        setProfile(r.profile);
        setGate("ready");
        setBridge({ status: "ok", serverUserId: r.profile.id });
        window.hideSuiteAuthModal?.();
        return;
      }
      cookieBootDoneRef.current = false;
      window.__chatSessionVerified = false;
      setProfile(null);
      if (r.forbidden) {
        showForbidden();
        return;
      }
      setGate("need_login");
      window.showSuiteAuthModal?.(undefined, { autoReconnect: false });
    });
    return () => {
      cancelled = true;
    };
  }, [showForbidden]);

  useEffect(() => {
    if (isChatHub2Desk()) return undefined;
    let cancelled = false;
    const run = async () => {
      if (!suiteUserId) {
        if (isChatStandalone()) return;
        window.__chatSessionVerified = false;
        setProfile(null);
        setGate("boot");
        return;
      }

      // Éviter un flash splash : ne pas redescendre en "checking" si déjà OK pour cet user
      const alreadyOk =
        window.__chatSessionVerified === true &&
        gateRef.current === "ready" &&
        profileRef.current != null &&
        String(profileRef.current.id) === String(suiteUserId);
      if (!alreadyOk) {
        setGate("checking");
      }
      const r = await fetchSsoVerify(suiteUserId);
      if (cancelled) return;
      if (r.ok && r.profile) {
        window.__chatSessionVerified = true;
        setProfile(r.profile);
        setGate("ready");
        // Comme Mail : pas de poll whoami en PWA standalone (boucle session lost).
        if (!isChatStandalone()) {
          window.startSuiteHubSessionWatch?.();
        }
        return;
      }
      window.__chatSessionVerified = false;
      setProfile(null);
      if (r.forbidden) {
        showForbidden();
        return;
      }
      setGate("error");
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [suiteUserId, showForbidden]);

  useEffect(() => {
    if (isChatStandalone() || isChatHub2Desk() || suiteUserId) return;
    const inIframe = isChatEmbeddedInHub();
    const delay = inIframe ? 2600 : 800;
    const t = window.setTimeout(() => {
      const id = readStoredSuiteUserId();
      if (id) {
        setSuiteUserId(id);
        return;
      }
      if (gateRef.current === "boot") {
        setGate("need_login");
      }
    }, delay);
    return () => window.clearTimeout(t);
  }, [suiteUserId]);

  /* Hors hub 2 seulement — le boot cookie a déjà fait chat-ping. */
  useEffect(() => {
    if (isChatHub2Desk()) return undefined;
    if (gate !== "ready" || !suiteUserId) {
      setBridge({ status: "idle" });
      return undefined;
    }
    let cancelled = false;
    setBridge({ status: "loading" });
    void fetchChatPing(suiteUserId).then((r) => {
      if (cancelled) return;
      if (r.success && typeof r.userId === "number") {
        resetChatSessionGateEmit();
        setBridge({
          status: "ok",
          serverUserId: r.userId,
          message: typeof r.message === "string" ? r.message : undefined,
        });
      } else {
        const unauthorized = isSuiteSessionErrorCode(r.error);
        if (unauthorized) {
          handleSuiteSessionLost(r.message);
          return;
        }
        if (r.error === "no_active_client") {
          showForbidden();
          return;
        }
        setBridge({
          status: "error",
          message: r.message || r.error || "Erreur passerelle",
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [gate, suiteUserId, handleSuiteSessionLost, showForbidden]);

  const sessionOk = Boolean(chatReady && profile && suiteUserId);
  const isFailure =
    gate === "forbidden" ||
    gate === "need_login" ||
    gate === "error" ||
    bridge.status === "error";
  const isBooting = !sessionOk && !isFailure;

  const [splashExiting, setSplashExiting] = useState(false);
  const [splashDone, setSplashDone] = useState(false);
  const splashBornRef = useRef(0);

  useEffect(() => {
    if (!isBooting) return;
    // Une seule naissance par cycle de boot (évite de rejouer l’intro)
    if (splashBornRef.current < 1) {
      splashBornRef.current = Date.now();
    }
    setSplashExiting(false);
    setSplashDone(false);
  }, [isBooting]);

  useEffect(() => {
    if (!sessionOk || isFailure || splashExiting || splashDone) return;
    // Hub : splash court — la vérif cookie suffit. PWA : intro un peu plus longue.
    const MIN_MS = isChatHub2Desk() || isChatEmbeddedInHub() ? 420 : 1550;
    const born = splashBornRef.current || Date.now();
    const elapsed = Date.now() - born;
    const wait = Math.max(0, MIN_MS - elapsed);
    const t = window.setTimeout(() => setSplashExiting(true), wait);
    return () => window.clearTimeout(t);
  }, [sessionOk, isFailure, splashExiting, splashDone]);

  useEffect(() => {
    if (!splashExiting) return;
    const t = window.setTimeout(() => {
      setSplashDone(true);
      splashBornRef.current = 0;
    }, 420);
    return () => window.clearTimeout(t);
  }, [splashExiting]);

  useEffect(() => {
    if (!isBooting) return;
    // Hub 2 : whoami+ping avec retries (jusqu’à ~25 s) — ne pas couper trop tôt.
    const maxMs = isChatHub2Desk() ? 32_000 : 18_000;
    const t = window.setTimeout(() => {
      if (gateRef.current === "ready") {
        setBridge({
          status: "error",
          message: "Délai dépassé — impossible de joindre le Chat.",
        });
        return;
      }
      setGate("need_login");
      window.showSuiteAuthModal?.(undefined, { autoReconnect: false });
    }, maxMs);
    return () => window.clearTimeout(t);
  }, [isBooting]);

  if (sessionOk && splashDone) {
    return (
      <ChatThemeProvider>
        <ChatShell suiteUserId={suiteUserId!} profile={profile!} />
      </ChatThemeProvider>
    );
  }

  const statusLabel =
    gate === "forbidden"
      ? "Accès refusé"
      : gate === "need_login"
        ? "Connexion requise"
        : gate === "error"
          ? "Connexion impossible"
          : bridge.status === "error"
            ? bridge.message || "Erreur passerelle"
            : gate === "checking" || bridge.status === "loading"
              ? "Vérification de la session…"
              : "Démarrage…";

  const splashMode = isFailure ? "status" : "loading";

  return (
    <ChatThemeProvider>
      <div className="chat-app min-h-dvh">
        <ChatBootSplash
          label={statusLabel}
          mode={splashMode}
          exiting={splashExiting && sessionOk}
        />
      </div>
    </ChatThemeProvider>
  );
}
